import { Peeky } from '@front/peeky';
import { PeekyEmitter } from '@shared/peeky.emitter';
import {
  DatagraphElement,
  Relation,
  setValNoFocus,
  updatePerfectScroll,
} from './editor';
import { Editor } from './editor';
import * as _dom from '@front/dom';
import { FocusObserver } from '@front/dom/FocusObserver';
import { sleep } from '@shared/sleep';
import { Fontpicker } from './fontpicker';
import { requestAnimationFramePromise } from '@shared/requestanimationframepromise';
import pkalert from './pkalert';

import { TextEditor } from './texteditor';

const cssSelectorMobileTablet = '@media only screen and (max-width: 1023px)';
const cssSelectorMobileDesktop =
  '@media only screen and (max-width: 767px) or (min-width: 1024px)';
const cssSelectorMobile = '@media only screen and (max-width: 767px)';
const cssSelectorTabletDesktop = '@media only screen and (min-width: 768px)';
const cssSelectorTablet =
  '@media only screen and (min-width: 768px) and (max-width: 1023px)';
const cssSelectorDesktop = '@media only screen and (min-width: 1024px)';

declare global {
  var pk: Peeky;
}

type keyObjType = {
  direction: string; // || '><',
  idorkey: string; // || 'key',
  idkeyvalue: string;
  type: string;
  param: string;
  unique: string;
  func: string; // || 'default',
  fullkey: string;
  partkey: string;
  element?: EditorElement;
  objList?: keyObjType[];
};

type updateUIListItem = {
  $dom: JQuery;
  keyobj: keyObjType;
  smooth?: boolean;
};

type customFunctionType = {
  init?: (
    this: EditorElement,
    $dom: JQuery,
    keyobj: keyObjType,
    smooth: boolean
  ) => void | Promise<void>;
  frontend?: (
    this: EditorElement,
    $dom: JQuery,
    keyobj: keyObjType,
    event?: any,
    smooth?: boolean
  ) => void | Promise<void>;
  backend?: (
    this: EditorElement,
    $dom: JQuery,
    keyobj: keyObjType,
    event?: any,
    smooth?: boolean
  ) => void | Promise<void>;
};

let CustomFunctions: { [value: string]: customFunctionType } = {
  default: {
    init: function ($dom, keyobj) {
      // Add "file" overlays
      let that = this;
      let param = keyobj.param;
      let insertvalues = $dom.data('insertvalues');
      let insertpreset = $dom.data('insertpreset');
      let data;
      let properties;
      if (!insertvalues) return;

      $.each(insertvalues, function (field, fielddata) {
        if (insertvalues[field].type === 'file') {
          (function insertInput() {
            let $input = $('<input type="file" multiple="multiple">');
            $input.css({
              position: 'absolute',
              top: 0,
              left: 0,
              opacity: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
            });
            $dom.append($input);

            // Force doubleclick since IE requires this to open file dialog
            if (
              /(msie) ([\w.]+)/.test(window.navigator.userAgent.toLowerCase())
            ) {
              $input.one('mousedown', function dblclick() {
                $input.trigger('click');
                $input.one('mousedown', dblclick);
              });
            }

            // on change, replace it with a new one and submit
            $input.one('change', function () {
              // Replace file input with a new one
              $input.detach();
              insertInput();

              // Upload multiple files
              let filelist = (<HTMLInputElement>this).files;
              if (filelist) {
                $.each(filelist, function (index, file) {
                  that.relate(
                    param,
                    {
                      entity: $dom.data('entity'),
                      entityVariant: $dom.data('entityvariant'),
                      file: file,
                      data: $.extend(
                        { filepath: file.name, title: file.name },
                        data || {}
                      ),
                      properties: $.extend(
                        {
                          _filename: file.name,
                          filepath: file.name,
                          title: file.name,
                        },
                        properties || {}
                      ),
                    },
                    true,
                    $dom
                  );
                });
              } else {
                // File API not supported
                that.relate(
                  param,
                  {
                    entity: $dom.data('entity'),
                    entityVariant: $dom.data('entityvariant'),
                    file: $input,
                    data: data,
                    properties: properties,
                  },
                  true,
                  $dom
                );
              }

              that.updateUI('related.' + param, true);
            });
          })();
        } else if (
          insertvalues[field].type === 'value' ||
          !insertvalues[field].type
        ) {
          if (!data) data = {};
          data[field] = insertvalues[field].value;
        } else if (insertvalues[field].type === 'property') {
          if (!properties) properties = {};
          properties[field] = insertvalues[field].value;
        }
      });
    },
    backend: function ($dom, keyobj) {
      let that = this;
      let type = keyobj.type;
      let param = keyobj.param;
      let value;

      if (type === 'insert') {
        // Get value from jQuery objects
        if ($dom.is(':text') || $dom.is('textarea')) value = $dom.val();
        else if ($dom.is(':checkbox')) value = $dom.prop('checked');
        else value = $dom.html();
        this.data(param, value, undefined, $dom.data('reload'));
      } else if (type === 'property') {
        // Get value from jQuery objects
        if ($dom.is(':text, textarea')) value = $dom.val();
        else if ($dom.is(':checkbox')) value = $dom.prop('checked');
        else value = $dom.html();
        this.property(param, value, undefined, $dom.data('reload'));
      } else if (type === 'relate') {
        // Check if some initial data is required for relation!
        let insertvalues = $dom.data('insertvalues');
        let insertpreset = $dom.data('insertpreset');
        let entity = $dom.data('entity');
        let entityVariant = $dom.data('entityvariant');

        if (insertpreset) {
          if (typeof insertpreset !== 'object') {
            console.log('Invalid insertpreset');
            console.log(insertpreset);
            return;
          }
          insertpreset.param = param;
          insertpreset.entity = insertpreset.entity || entity;
          insertpreset.entityVariant =
            insertpreset.entityVariant || entityVariant;

          let searchForFile = (preset) => {
            for (let k in preset) {
              if (typeof preset[k] === 'object') {
                if (searchForFile(preset[k])) {
                  return true;
                }
              } else {
                if (k === 'file' && preset[k] === true) {
                  return true;
                }
              }
            }
            return false;
          };

          let getFiles = (callback) => {
            let $fileDialog = $dom.data('$fileDialog');
            if (!$fileDialog) {
              $fileDialog = $('<input type="file" multiple>');
              $fileDialog.css({
                position: 'absolute',
                top: -9999,
                left: -9999,
              });
              $dom.after($fileDialog);
              $dom.data('$fileDialog', $fileDialog);
            }
            function change(this: HTMLInputElement) {
              let files = this.files;
              callback(files);
              $fileDialog.off('reset', reset);
            }
            function reset() {
              $fileDialog.off('change', change);
              callback();
            }
            $fileDialog.one('click', function () {
              $fileDialog.one('change', change);
              $fileDialog.one('reset', reset);
            });
            $fileDialog.trigger('reset'); // Reset potential old call
            $fileDialog.click();
          };

          let createElement = function (preset, files, fileNo) {
            if (preset._id) {
              return this.editor.elementIndexId[preset._id];
            }
            let element = new EditorElement(
              this.editor.getkey(),
              {
                entity: preset.entity,
                entityVariant: preset.entityVariant,
                parentSite: this.entity === 'site' ? this : this.parentSite,
              },
              this,
              this.editor,
              undefined
            );
            for (let k in preset.data) {
              element.data(k, preset.data[k]);
            }
            for (let k in preset.properties) {
              element.property(k, preset.properties[k]);
            }
            if (preset.file) {
              element.savefile('file', files[fileNo || 0]);
            }
            for (let k in preset.relate) {
              insert.call(element, preset.relate[k], files, fileNo);
            }
            return element;
          };

          let insert = function (preset, files: FileList, fileNo: number) {
            let element: EditorElement;
            if (preset.clone === 'files') {
              for (let i = 0; i < files.length; i++) {
                element = createElement.call(this, preset, files, i);
                this.relate(preset.param, element, true, $dom);
              }
            } else {
              element = createElement.call(this, preset, files, fileNo);
              this.relate(preset.param, element, true, $dom);
            }
            return element;
          };

          if (searchForFile(insertpreset)) {
            getFiles(function (files: FileList) {
              if (!files) {
                return;
              }
              let element = insert.call(that, insertpreset, files);
              that.updateUI('related.' + param, true);
              that.goTo([param + ':' + element.key]);
            });
          } else {
            let element = insert.call(that, insertpreset);
            that.updateUI('related.' + param, true);
            that.goTo([param + ':' + element.key]);
          }
        } else if (insertvalues) {
          (function () {
            let $selector: JQuery;
            let data;
            let properties;
            let nosubmit;

            // On second-click, close the dropdown
            if ($dom.data('dropped')) {
              $dom.removeClass('focus');
              return;
            }

            // Generate form / popup file picker etc
            $.each(insertvalues, function (field, fielddata) {
              if (insertvalues[field].type === 'enum') {
                $dom.addClass('focus');
                $selector = $('<div class="choiceDropdown"></div>');
                $.each(
                  insertvalues[field].choices,
                  function (choice: string, value) {
                    let $choice = $(
                      '<span class="padded listItem focus bgfield choice block">' +
                        '<span class="tinythumb left"><span class="sprite _' +
                        choice +
                        '"></span></span>' +
                        '<span class="right">' +
                        insertvalues[field].choices[choice] +
                        '</span>' +
                        '</span>'
                    );
                    $choice.one('click', function () {
                      if (insertvalues[field].target === 'property') {
                        if (!properties) properties = {};
                        properties[field] = choice;
                      } else {
                        if (!data) data = {};
                        data[field] = choice;
                      }
                      let element = that.relate(
                        param,
                        {
                          entity: $dom.data('entity'),
                          entityVariant: $dom.data('entityvariant'),
                          data: data,
                          properties: properties,
                        },
                        true,
                        $dom
                      );

                      // Open this entry
                      that.goTo([param + ':' + element.key]);

                      // UpdateUI after entry has been opened
                      that.updateUI('related.' + param, true);
                      $selector.slideUp(100, function () {
                        $dom.data('dropped', false);
                        $dom.removeClass('focus');
                        $selector.remove();
                      });
                    });
                    $selector.append($choice);
                  }
                );
              } else if (
                insertvalues[field].type === 'value' ||
                !insertvalues[field].type
              ) {
                if (!data) data = {};
                data[field] = insertvalues[field].value;
              } else if (insertvalues[field].type === 'property') {
                if (!properties) properties = {};
                properties[field] = insertvalues[field].value;
              } else if (insertvalues[field].type === 'file') {
                nosubmit = true;
              }

              if (data) {
              }
            });

            if (!nosubmit && $selector) {
              $selector.hide();
              $dom.after($selector);
              $selector.slideDown(100);
              $dom.data('dropped', true);
              $dom.closest('body').one('click', function remove(e) {
                if (
                  $(e.target).parents().add(e.target).filter($selector).length >
                  0
                )
                  return $dom.closest('body').one('click', remove);
                $selector.slideUp(100, function () {
                  $dom.data('dropped', false);
                  $dom.removeClass('focus');
                  $selector.remove();
                });
              });
            } else if (!nosubmit && (data || properties)) {
              let element = that.relate(
                param,
                {
                  entity: $dom.data('entity'),
                  entityVariant: $dom.data('entityvariant'),
                  data: data,
                  properties: properties,
                },
                true,
                $dom
              );
              if (element.entitydata('render')._form && !$dom.data('nogoto')) {
                that.goTo([param + ':' + element.key]);
              }
            }
          })();
        } else {
          let relateTo;
          if ($dom.data('relate')) {
            relateTo = this.editor.elementIndexKey[$dom.data('relate')];
          } else {
            relateTo = {
              entity: $dom.data('entity'),
              entityVariant: $dom.data('entityvariant'),
            };
          }
          let element = this.relate(
            param,
            relateTo,
            $dom.data('submit') === 'false' ? false : undefined,
            $dom
          );
          if (element.entitydata('render')._form && !$dom.data('nogoto')) {
            that.goTo([param + ':' + element.key]);
          }
        }
      } else if (type === 'unrelate') {
        let unrelateFrom;
        if ($dom.data('unrelate')) {
          unrelateFrom = this.editor.elementIndexKey[$dom.data('unrelate')];
        } else {
          unrelateFrom = this.parent;
        }
        this.unrelate(param, unrelateFrom, $dom);
      } else if (type === 'file') {
        let dom = $dom[0];
        if (dom instanceof HTMLInputElement && dom.type === 'file') {
          let file = dom.files[0];
          this.savefile(param, file);
        }
      }
    },
  },

  markdown: {
    init: function ($dom, keyobj) {},
    backend: function ($dom, keyobj) {},
    frontend: async function ($dom, keyobj) {
      let markdown = this.data(keyobj.param) || '';
      let { default: marked } = await import(
        /* webpackChunkName: "marked" */ 'marked'
      );
      $dom.html(marked(markdown));
    },
  },

  dateFormat: {
    init: function ($dom, keyobj) {},
    backend: function ($dom, keyobj) {},
    frontend: function ($dom, keyobj) {
      let date = this.value(keyobj) || '';
      let format =
        $dom.data('dateFormat') ||
        $dom.data('dateformat') ||
        '[Mon] [DD], [YYYY]';
      let formatted;

      if (typeof date === 'string') {
        //date = new Date(date);
        //splitDate = (/^(\d{4})\-(\d\d)\-(\d\d)([tT]((\d\d):(\d\d):(\d\d)))?$/).exec(date) || [];
        //date = Date.UTC(splitDate[1], splitDate[2]-1, splitDate[3], splitDate[6] || 0, splitDate[7] || 0, splitDate[8] || 0);
        date = new Date(date);
        if (date.toString() === 'Invalid Date') {
          formatted = '';
        }
      }
      if (!formatted) {
        formatted = format.replace(
          /\[(YYYY|YY|MM|M|DD|D|DDth|hh|mm|ss|Month|month|MONTH|Mon|mon|MON|Day|day|DAY)]/g,
          function (s, key) {
            let months = [
              'January',
              'February',
              'March',
              'April',
              'May',
              'June',
              'July',
              'August',
              'September',
              'October',
              'November',
              'December',
            ];
            let days = [
              'Monday',
              'Tuesday',
              'Wednesday',
              'Thursday',
              'Friday',
              'Saturday',
              'Sunday',
            ];
            switch (key) {
              case 'YYYY':
                return date.getUTCFullYear();
                break;
              case 'YY':
                return ('' + date.getUTCFullYear()).slice(-2);
                break;
              case 'MM':
                return ('0' + (date.getUTCMonth() + 1)).slice(-2);
                break;
              case 'M':
                return date.getUTCMonth() + 1;
                break;
              case 'DD':
                return ('0' + date.getUTCDate()).slice(-2);
                break;
              case 'D':
                return date.getUTCDate();
                break;
              case 'DDth':
                let dayno = date.getDate();
                let ones = dayno % 10;
                let tens = Math.floor(dayno / 10) % 10;
                if (tens === 1) {
                  return dayno + 'th';
                } else {
                  switch (ones) {
                    case 1:
                      return date.getUTCDate() + 'st';
                      break;
                    case 2:
                      return date.getUTCDate() + 'nd';
                      break;
                    case 3:
                      return date.getUTCDate() + 'rd';
                      break;
                    default:
                      return date.getUTCDate() + 'th';
                  }
                }
                break;
              case 'hh':
                return ('0' + date.getUTCHours()).slice(-2);
                break;
              case 'mm':
                return ('0' + date.getUTCMinutes()).slice(-2);
                break;
              case 'ss':
                return ('0' + date.getUTCSeconds()).slice(-2);
                break;
              case 'Mon':
                return (months[date.getUTCMonth()] || '').substring(0, 3);
                break;
              case 'mon':
                return (months[date.getUTCMonth()] || '')
                  .substring(0, 3)
                  .toLowerCase();
                break;
              case 'MON':
                return (months[date.getUTCMonth()] || '')
                  .substring(0, 3)
                  .toUpperCase();
                break;
              case 'Month':
                return months[date.getUTCMonth()] || '';
                break;
              case 'month':
                return (months[date.getUTCMonth()] || '').toLowerCase();
                break;
              case 'MONTH':
                return (months[date.getUTCMonth()] || '').toUpperCase();
                break;
              case 'Day':
                return days[date.getUTCDay()] || '';
                break;
              case 'day':
                return (days[date.getUTCDay()] || '').toLowerCase();
                break;
              case 'DAY':
                return (days[date.getUTCDay()] || '').toUpperCase();
                break;
              default:
                return s;
            }
          }
        );
      }

      $dom.html(formatted);
    },
  },

  sellablePrice: {
    init: function () {},
    backend: function ($dom, keyobj) {
      let value;
      if ($dom.is(':input')) value = $dom.val();
      else value = $dom.html();
      value = parseFloat(value) * 100;

      if (isNaN(value)) {
        value = '';
      }

      if (keyobj.type === 'insert') {
        this.data(keyobj.param, value);
      } else if (keyobj.type === 'property') {
        this.property(keyobj.param, value);
      }
    },
    frontend: function ($dom, keyobj) {
      let value = this.value(keyobj);
      let formatted;

      if (value === '' || typeof value === 'undefined') {
        formatted = '';
      } else {
        let price = parseInt(this.value(keyobj) || '0', 10);
        let format = $dom.data('priceformat') || '[int],[dec]';
        let int = Math.floor(price / 100);
        let decimal: string | number = price - int * 100;
        if (decimal < 10) {
          decimal = '0' + decimal;
        }
        formatted = format.replace(/\[int\]/, int).replace(/\[dec\]/, decimal);
      }

      if ($dom.is(':input')) {
        $dom.val(formatted);
      } else {
        $dom.html(formatted);
      }
    },
  },

  editable: {
    init: async function ($dom, keyobj) {
      let tagMap = $dom.data('schema');
      if (!tagMap) {
        if ($dom.is('h1,h2,h3,h4')) {
          tagMap = {};
          //tagMap.paragraph = true;
        }
      }

      const { TextEditor } = await import(
        /* webpackChunkName: "pk-texteditor" */ './texteditor'
      );
      let texteditor = new TextEditor($dom[0], tagMap);
      $dom.data('texteditor', texteditor);

      texteditor.on('change', () => {
        $dom.trigger('pkchange');
      });

      $dom.on('focus', () => {
        $dom.data('disablehide', true);
      });
      $dom.on('blur', () => {
        $dom.removeData('disablehide');
        $dom.trigger('pkchange');
      });
    },

    backend: async function ($dom, keyobj) {
      let texteditor: TextEditor = $dom.data('texteditor');
      if (!texteditor) {
        return;
      }

      let markdown = texteditor.toMarkdown();

      // Get data from prosemirror-instance
      if (keyobj.type === 'insert') {
        this.data(keyobj.param, markdown);
      } else if (keyobj.type === 'property') {
        this.property(keyobj.param, markdown);
      }
    },

    frontend: async function ($dom, keyobj, event) {
      let texteditor: TextEditor = $dom.data('texteditor');
      if (!texteditor) {
        return;
      }
      if (event && event.type !== 'pkchange') {
        let markdown = this.value(keyobj) ?? '';
        texteditor.fromMarkdown(markdown);
      }
    },
  },

  gfm: {
    init: async function ($dom, keyobj) {},
    backend: function ($dom, keyobj) {
      let value;
      if ($dom.is(':input')) value = $dom.val();
      else value = $dom.html();
      if (keyobj.type === 'insert') {
        this.data(keyobj.param, value);
      } else if (keyobj.type === 'property') {
        this.property(keyobj.param, value);
      }
    },
    frontend: async function ($dom, keyobj) {
      let input;
      if (keyobj.type === 'property') input = this.property(keyobj.param);
      else input = this.data(keyobj.param);
      let markdown = input || '';
      let inline = $dom.data('gfm-inline');
      let { default: marked } = await import(
        /* webpackChunkName: "marked" */ 'marked'
      );

      if (inline) {
        $dom.html(
          (<any>marked).parseInline(markdown, { gfm: true, breaks: true })
        );
      } else {
        $dom.html(marked(markdown, { gfm: true, breaks: true }));
      }
    },
  },

  fileDrop: {
    init: function ($dom, keyobj) {
      let that = this;
      let param = keyobj.param;
      let $group = $dom.closest('.group');
      let $expandable = $group.find('.dropdown:first');
      let $collection = $dom.closest('.collection');
      let $link = $group.find('.dropdownlink');
      let $target: JQuery;
      let over = false;
      let closed = false;

      if (!$group.data('droppable')) {
        $group
          .on('dragenter', function (e) {
            if (over) return;
            over = true;
            $(this).addClass('hover');
            // Slide up if closed
            if (!$expandable.is(':visible')) {
              closed = true;
              $link.trigger('customclick', true);
            }
            return false;
          })
          .on('dragout', function (e) {
            over = false;
            $(this).removeClass('hover');
            // Slide down if initially closed
            if (closed) {
              closed = false;
              $link.trigger('customclick', true);
            }
            return false;
          });
        $group.data('droppable', true);
      }

      //if ($collection.length > 0) $target = $collection;
      //else $target = $dom;
      $target = $group;

      $target.on('drop', function (e) {
        let event = e.originalEvent;
        let files = event.dataTransfer.files;
        $.each(files, function (index, file) {
          that.relate(
            param,
            {
              entity: $dom.data('entity'),
              entityVariant: $dom.data('entityvariant'),
              file: file,
            },
            true,
            $dom
          );
        });
        that.updateUI('related.' + param, true);

        closed = false;

        return false;
      });
    },

    backend: function () {},
    frontend: function () {},
  },

  css: {
    init: function ($dom, keyobj) {},

    backend: function ($dom, keyobj, event) {
      let fullParam = keyobj.param;
      let rarray = /(.*?-)?(.*)/.exec(fullParam);
      let prefix = rarray[1] || '';
      let param = rarray[2];
      let val;

      // Get value from jQuery objects
      if ($dom.is(':text')) val = $dom.val();
      else if ($dom.is(':checkbox')) val = $dom.prop('checked');
      else val = $dom.html();

      if (param === 'top' && this.property(prefix + param) !== val) {
        val = parseFloat(val) || 0;
        this.property(prefix + 'top', val);
        this.property(prefix + 'bottom', '');
        this.property(prefix + 'middle', '');
        this.updateUI('property.' + prefix + 'bottom');
        this.updateUI('property.' + prefix + 'middle');
      } else if (param === 'bottom' && this.property(prefix + param) !== val) {
        val = parseFloat(val) || 0;
        this.property(prefix + 'bottom', val);
        this.property(prefix + 'top', '');
        this.property(prefix + 'middle', '');
        this.updateUI(prefix + 'property.' + prefix + 'top');
        this.updateUI(prefix + 'property.' + prefix + 'middle');
      } else if (param === 'middle' && this.property(prefix + param) !== val) {
        val = parseFloat(val) || 0;
        this.property(prefix + 'middle', val);
        this.property(prefix + 'top', '');
        this.property(prefix + 'bottom', '');
        this.updateUI('property.' + prefix + 'top');
        this.updateUI('property.' + prefix + 'bottom');
      } else if (param === 'left' && this.property(prefix + param) !== val) {
        val = parseFloat(val) || 0;
        this.property(prefix + 'left', val);
        this.property(prefix + 'right', '');
        this.property(prefix + 'center', '');
        this.updateUI('property.' + prefix + 'right');
        this.updateUI('property.' + prefix + 'center');
      } else if (param === 'right' && this.property(prefix + param) !== val) {
        val = parseFloat(val) || 0;
        this.property(prefix + 'right', val);
        this.property(prefix + 'left', '');
        this.property(prefix + 'center', '');
        this.updateUI('property.' + prefix + 'left');
        this.updateUI('property.' + prefix + 'center');
      } else if (param === 'center' && this.property(prefix + param) !== val) {
        val = parseFloat(val) || 0;
        this.property(prefix + 'center', val);
        this.property(prefix + 'right', '');
        this.property(prefix + 'left', '');
        this.updateUI('property.' + prefix + 'left');
        this.updateUI('property.' + prefix + 'right');
      } else {
        this.property(fullParam, val);
      }
    },
  },

  positionCss: {
    // Attach drag&drop
    init: function ($dom, keyobj) {
      // Only bind once
      if ($dom.data('_boundcss')) return;
      $dom.data('_boundcss', true);

      let that = this;
      let $body = $dom.closest('body');
      let $offsetParent = getRealOffsetParent($dom);
      $dom.data('$offsetParent', $offsetParent);

      this.$cssElement = $dom;

      $dom.on('mousedown', function (e) {
        let moved = false;
        let stopped = false;
        let startX = e.pageX;
        let startY = e.pageY;
        let domWidth = $dom.outerWidth();
        let domHeight = $dom.outerHeight();
        let position = $dom.position();
        position = {
          top:
            position.top +
            $offsetParent.scrollTop() +
            (parseInt($dom.css('marginTop'), 10) || 0),
          left:
            position.left +
            $offsetParent.scrollLeft() +
            (parseInt($dom.css('marginLeft'), 10) || 0),
        };
        let height = $offsetParent.height();
        let $widthHack = $('<div></div>').appendTo($offsetParent); // Hack to find the width of offsetParent without scrollbars
        let width = $widthHack.width();
        let $ps = $dom.closest('.ps');
        let allowCenter = $dom.data('allowcenter') || false;
        let rightWidth = allowCenter ? (width / 2) * 1.05 : width / 2;
        let centerWidth = allowCenter ? (width / 2) * 0.95 : 0;
        let bottomHeight = allowCenter ? (height / 2) * 1.05 : height / 2;
        let middleHeight = allowCenter ? (height / 2) * 0.95 : 0;
        $widthHack.remove();

        function move(e) {
          var diffX = e.pageX - startX,
            diffY = e.pageY - startY,
            left = position.left + diffX,
            top = position.top + diffY;

          if (!moved && Math.abs(diffX) + Math.abs(diffY) > 5) {
            moved = true;
            $dom.data('moving', true);
          }

          // Set right css
          if (left + domWidth / 2 > rightWidth) {
            that.property('left', '');
            that.property('center', '');
            if (that.property('rightUnit') === '%')
              that.property(
                'right',
                (((width - (left + domWidth)) / width) * 100).toFixed(4)
              );
            // Percent
            else that.property('right', width - (left + domWidth));
            that.updateUI('property.left', false);
            that.updateUI('property.center', false);
            that.updateUI('property.right');
          }
          // Set center css
          else if (allowCenter && left + domWidth / 2 > centerWidth) {
            that.property('left', '');
            that.property('right', '');
            if (that.property('centerUnit') === '%')
              that.property(
                'center',
                (((left - width / 2 + domWidth / 2) / width) * 100).toFixed(4)
              );
            else that.property('center', left - width / 2 + domWidth / 2);
            that.updateUI('property.center');
            that.updateUI('property.left', false);
            that.updateUI('property.right', false);
          }
          // Set left css
          else {
            that.property('right', '');
            that.property('center', '');
            if (that.property('leftUnit') === '%')
              that.property('left', ((left / width) * 100).toFixed(4));
            else that.property('left', left.toString());
            that.updateUI('property.left');
            that.updateUI('property.center', false);
            that.updateUI('property.right', false);
          }

          // Set bottom
          if (top + domHeight / 2 > bottomHeight) {
            that.property('top', '');
            that.property('middle', '');
            if (that.property('bottomUnit') === '%')
              that.property(
                'bottom',
                (((height - (top + domHeight)) / height) * 100).toFixed(4)
              );
            else that.property('bottom', height - (top + domHeight));
            that.updateUI('property.top', false);
            that.updateUI('property.middle', false);
            that.updateUI('property.bottom');
          }
          // Set middle
          else if (allowCenter && top + domHeight / 2 > middleHeight) {
            that.property('top', '');
            that.property('bottom', '');
            if (that.property('middleUnit') === '%')
              that.property(
                'middle',
                (((top - height / 2 + domHeight / 2) / height) * 100).toFixed(4)
              );
            else that.property('middle', top - height / 2 + domHeight / 2);
            that.updateUI('property.top', false);
            that.updateUI('property.bottom', false);
            that.updateUI('property.middle');
          }
          // Set top
          else {
            that.property('bottom', '');
            that.property('middle', '');
            if (that.property('topUnit') === '%')
              that.property('top', ((top / height) * 100).toFixed(4));
            else that.property('top', top);
            that.updateUI('property.bottom', false);
            that.updateUI('property.middle', false);
            that.updateUI('property.top');
          }

          updatePerfectScroll($ps);

          //window.requestAnimationFrame(function() {
          setTimeout(function () {
            if (!stopped) $body.one('mousemove', move);
          }, 25);

          return false;
        }
        $body.one('mousemove', move);
        $body.add($dom).one('mouseup', function mouseup() {
          $body.off('mousemove', move);
          stopped = true;
          $body.add($dom).off('mouseup', mouseup);
          $dom.data('moving', false);
          if (moved) return false;
        });

        e.preventDefault();
      });
    },

    frontend: function ($dom, keyobj, event, smooth) {
      var fullParam = keyobj.param,
        rarray = /(.*?-)?(.*)/.exec(fullParam),
        prefix = rarray[1] || '',
        param = rarray[2],
        val = this.property(fullParam),
        unit = this.property(fullParam + 'Unit') || 'px',
        css = <any>{},
        pre = <any>{},
        timer,
        position = $dom.position(),
        dimensions = _dom.getDimensions($dom),
        $offsetParent: JQuery = $dom.data('$offsetParent'),
        marginTop,
        marginLeft,
        parentWidth,
        parentHeight,
        win = _dom.getContainingWindow($dom[0]),
        flexiwidth = $dom.data('flexiwidth') || false,
        flexiheight = $dom.data('flexiheight') || false;

      // Cache in case it changes!
      if (dimensions.width > 0) $dom.data('dimWidth', dimensions.width);
      else dimensions.width = $dom.data('dimWidth') || 0;
      if (dimensions.height > 0) $dom.data('dimHeight', dimensions.height);
      else dimensions.height = $dom.data('dimHeight') || 0;

      if (typeof val === 'undefined' || val === '') return;

      val = parseFloat(val);
      if (isNaN(val)) return;

      // Preparation of common properties
      if (param === 'top' || param === 'bottom' || param === 'middle') {
        // cache parent height
        parentHeight = _dom.getDimensions($offsetParent).height;

        // Top-margin for centering
        marginTop = parseInt($dom.css('marginTop')) || 0;
        if (marginTop !== 0) {
          pre.marginTop = marginTop;
          if (param === 'top' || param === 'bottom') css.marginTop = 0;
        }
        if (param === 'middle') {
          pre.marginTop = marginTop || 0;
          if (unit !== '%' && this.property('heightUnit') !== '%')
            css.marginTop = val - dimensions.height / 2 + 'px';
          else if (unit !== '%') css.marginTop = val + 'px';
          else css.marginTop = -dimensions.height / 2 + 'px';
        }
      } else if (param === 'left' || param === 'right' || param === 'center') {
        // cache parent width
        parentWidth = _dom.getDimensions($offsetParent).width;

        // Left-margin for centering
        marginLeft = parseInt($dom.css('marginLeft')) || 0;
        if (marginLeft !== 0) {
          pre.marginLeft = marginLeft;
          if (param === 'left' || param === 'right') css.marginLeft = 0;
        }
        if (param === 'center') {
          pre.marginLeft = marginLeft || 0;
          if (unit !== '%' && this.property('widthUnit') !== '%')
            css.marginLeft = val - dimensions.width / 2 + 'px';
          else if (unit !== '%') css.marginLeft = val + 'px';
          else css.marginLeft = -dimensions.width / 2 + 'px';
        }
      }

      // TOP
      if (param === 'top') {
        if ($dom.css('top') === 'auto') {
          if (!flexiheight) pre.bottom = 'auto';
          if (unit === '%')
            pre.top = (position.top / parentHeight) * 100 + unit;
          else pre.top = position.top + unit;
        }
        css.top = val + unit;
        if (!flexiheight) css.bottom = 'auto';
      }

      // Bottom
      else if (param === 'bottom') {
        if ($dom.css('bottom') === 'auto') {
          if (!flexiheight) pre.top = 'auto';
          if (unit === '%')
            pre.bottom =
              ((parentHeight - position.top - dimensions.height) /
                parentHeight) *
                100 +
              unit;
          else
            pre.bottom = parentHeight - position.top - dimensions.height + unit;
        }
        css.bottom = val + unit;
        if (!flexiheight) css.top = 'auto';
      }

      // Middle
      else if (param === 'middle') {
        if ($dom.css('top') === 'auto') {
          pre.bottom = css.bottom = 'auto';
          pre.top = (position.top / parentHeight) * 100 + '%';
        }
        if (unit === '%' && this.property('heightUnit') === '%')
          css.top = 50 + val - this.property('height') / 2 + unit;
        else if (unit === '%') css.top = 50 + val + unit;
        else css.top = 50 + '%';
      }

      // Left
      else if (param === 'left') {
        if ($dom.css('left') === 'auto') {
          if (!flexiwidth) pre.right = 'auto';
          if (unit === '%')
            pre.left = (position.left / parentWidth) * 100 + unit;
          else pre.left = position.left + unit;
        }
        css.left = val + unit;
        if (!flexiwidth) css.right = 'auto';
      }

      // Right
      else if (param === 'right') {
        if ($dom.css('right') === 'auto') {
          if (!flexiwidth) pre.left = 'auto';
          if (unit === '%')
            pre.right =
              ((parentWidth - position.left - dimensions.width) / parentWidth) *
                100 +
              unit;
          else
            pre.right = parentWidth - position.left - dimensions.width + unit;
          if (pre.marginLeft) {
            pre.marginRight = -pre.marginLeft;
            pre.marginLeft = 0;
            css.marginRight = -css.marginLeft;
          }
        }
        css.right = val + unit;
        if (!flexiwidth) css.left = 'auto';
      }

      // Center
      else if (param === 'center') {
        if ($dom.css('left') === 'auto') {
          pre.right = css.right = 'auto';
          pre.left = (position.left / parentWidth) * 100 + '%';
        }
        if (unit === '%' && this.property('widthUnit') === '%')
          css.left = 50 + val - this.property('width') / 2 + unit;
        else if (unit === '%') css.left = 50 + val + unit;
        else css.left = 50 + '%';
      }

      // Set marker for middle / center values
      if (param === 'middle') $dom.data('middle', { value: val, unit: unit });
      else if (param === 'top' || param === 'bottom') $dom.removeData('middle');
      if (param === 'center') $dom.data('center', { value: val, unit: unit });
      else if (param === 'left' || param === 'right') $dom.removeData('center');

      if (
        param !== 'middle' &&
        param !== 'top' &&
        param !== 'bottom' &&
        param !== 'left' &&
        param !== 'right' &&
        param !== 'center'
      ) {
        pre[param] = $dom.css(param);
        css[param] = val + (typeof unit === 'undefined' ? 'px' : unit);
      }

      // Render
      if (smooth) {
        timer = $dom.data('timer');
        if (timer) clearTimeout(timer);
        timer = setTimeout(function () {
          $dom.stop();
          $dom.css(pre);
          setTimeout(function () {
            $dom.css({
              transition: 'all .2s ease-out',
              '-webkit-transition': 'all .2s ease-out',
            });
            $dom.css(css);
            setTimeout(function () {
              $dom.css({
                transition: '',
                '-webkit-transition': '',
              });
              (<any>win).$(win).resize();
            }, 300);
          });
          $dom.removeData('timer');
        }, 320);
        $dom.data('timer', timer);
      } else {
        $dom.css(css);
        (<any>win).$(win).resize();
      }
    },
  },

  sheetAlign: {
    init: function ($dom, keyobj) {},
    frontend: function ($dom, keyobj, smooth) {
      var that = this,
        fullParam = keyobj.param,
        rarray = /(.*?-)?(.*)/.exec(fullParam),
        prefix = rarray[1] || '',
        param = rarray[2],
        val = this.property(fullParam),
        align;

      setTimeout(function () {
        if (param === 'align') {
          if (val === 'left') {
            that.property(
              prefix + 'left',
              parseInt(that.property(prefix + 'left'), 10) || 0
            );
            that.property(prefix + 'right', 'auto');
            that.property(prefix + 'center', '');
            that.updateUI('property.' + prefix + 'left', true);
          } else if (val === 'right') {
            that.property(
              prefix + 'right',
              parseInt(that.property(prefix + 'right'), 10) || 0
            );
            that.property(prefix + 'left', 'auto');
            that.property(prefix + 'center', '');
            that.updateUI('property.' + prefix + 'right', true);
          } else {
            that.property(prefix + 'right', '0');
            that.property(prefix + 'left', '0');
            that.property(prefix + 'center', '0');
            that.updateUI('property.' + prefix + 'center', true);
          }
        } else {
          align = that.property(prefix + 'align');
          if (param === 'left') {
            if (align === 'center' || align === 'right') {
              // Disable
              $dom.prop('disabled', true);
              $dom.parent().addClass('disabled');
            } else {
              // Enable
              $dom.prop('disabled', false);
              $dom.parent().removeClass('disabled');
            }
          } else if (param === 'right') {
            if (align === 'center' || align === 'left') {
              // Disable
              $dom.addClass('disabled').prop('disabled', true);
              $dom.parent().addClass('disabled');
            } else {
              // Enable
              $dom.removeClass('disabled').prop('disabled', false);
              $dom.parent().removeClass('disabled');
            }
          }
        }
      });
    },
    backend: function ($dom, keyobj) {},
  },

  updateGrid: {
    init: function ($dom, keyobj) {},
    frontend: function ($dom, keyobj) {
      const dom = $dom[0];
      const gridProperties = $dom.data('gridproperties');
      let timer = $dom.data('reloadTimer');
      let value = this.value(keyobj);
      let propertyName = keyobj.param || '';
      if (gridProperties) {
        propertyName = gridProperties[propertyName] || propertyName || '';
      }
      propertyName = propertyName.toLowerCase();

      if (_dom.data(dom, propertyName) != value) {
        if (typeof _dom.data(dom, '-' + propertyName) === 'undefined') {
          _dom.data(dom, '-' + propertyName, _dom.data(dom, propertyName));
        }
        if (typeof value === 'undefined') {
          value = _dom.data(dom, '-' + propertyName);
        }
        _dom.data(dom, propertyName, value);
        if (!timer) {
          timer = setTimeout(() => {
            $dom.removeData('reloadTimer');
            dom.dispatchEvent(new Event('reloadGrid'));
          }, 250);
          $dom.data('reloadTimer', timer);
        }
      }
    },
    backend: function ($dom, keyobj) {},
  },

  cssUnit: {
    init: function ($dom, keyobj) {
      $dom.css('position', 'relative');

      var that = this,
        $px = $('<span>px</span>'),
        $pc = $('<span>%</span>'),
        $both = $px.add($pc),
        rarray = /(.*?-)?(.*)/.exec(keyobj.param),
        prefix = rarray[1] || '',
        param = rarray[2],
        defaultUnit = $dom.text();
      $both.css('display', 'block');
      $dom.css('display', 'inline-block');
      $dom.html('').append($pc).append($px);

      function change() {
        var $old = $(this),
          $new = $both.not(this),
          newUnit = $new.text() || defaultUnit,
          oldValue = parseInt(that.property(keyobj.param), 10),
          $element = that.$cssElement,
          $lengthHack: JQuery,
          totalLength;

        if (!$element || isNaN(oldValue)) return;

        if (typeof oldValue !== 'undefined') {
          if (param === 'left' || param === 'right') {
            $lengthHack = $('<div></div>').appendTo(
              getRealOffsetParent($element)
            ); // Hack to find the width of offsetParent without scrollbars
            totalLength = $lengthHack.width();
            $lengthHack.remove();
          } else {
            totalLength = getRealOffsetParent($element).height();
          }

          // Re-calculate between % and px
          if (newUnit === '%')
            that.property(
              keyobj.param,
              ((oldValue / totalLength) * 100).toFixed(4)
            );
          else
            that.property(
              keyobj.param,
              Math.round((oldValue * totalLength) / 100)
            );
        }

        that.property(keyobj.param + 'Unit', newUnit);
        $old.hide();
        $new.show();
        that.updateUI(keyobj.fullkey);
      }
      function activate() {
        $both.not(this).hide();
        $(this).show();
      }

      if (this.property(keyobj.param + 'Unit') === '%') activate.call($pc);
      else activate.call($px);

      $both.on('click', function () {
        var that = this;
        setTimeout(function () {
          change.call(that);
        });
      });
    },
    backend: function ($dom, keyobj) {},
    frontend: function ($dom, keyobj) {},
  },

  generatehost: {
    backend: function ($dom, keyobj, event) {
      if (event.type !== 'change') return;

      // If the "wanted" field is set, the user has requested a specific host/key
      let wantedField = $dom.data('wantedField') || 'wantedKey';
      if (this.data(wantedField) || this.property(wantedField)) {
        return;
      }

      // Filter the value based on certain rules
      let val = $dom.val().toString();
      val = val.toLowerCase();
      val = val.replace(/ /g, '-').replace(/[^a-zA-Z0-9_-]/g, '');

      // Only update data if the value is changed
      if (keyobj.type === 'insert') {
        if (this.data(keyobj.param) !== val) {
          this.data(keyobj.param, val);
        }
      } else if (keyobj.type === 'property') {
        if (this.property(keyobj.param) !== val) {
          this.property(keyobj.param, val);
        }
      }
    },
  },

  placeholder: {
    backend: function () {},
    frontend: function ($dom, keyobj, event) {
      var value = this.value(keyobj);
      if (typeof value !== 'undefined' && $dom.attr('placeholder') !== value) {
        if ($dom.attr('placeholder') && !$dom.data('placeholder')) {
          $dom.data('placeholder');
        }
        $dom.attr('placeholder', value);
        $dom.trigger('placeholder');
      } else if (
        typeof value === 'undefined' &&
        $dom.data('placeholder') &&
        $dom.data('placeholder') !== $dom.attr('placeholder')
      ) {
        $dom.attr('placeholder', $dom.data('placeholder'));
        $dom.trigger('placeholder');
      }
    },
  },

  backgroundColor: {
    backend: function () {},
    frontend: function ($dom, keyobj, event) {
      var value = this.value(keyobj);
      if (typeof value !== 'undefined') {
        // Find lightness, set foreground accordingly
        // 0.299*R + 0.587*G + 0.114*B)
        $dom.css('background', value);
      }
    },
  },

  colorPreview: {
    init: function ($dom, keyobj) {
      var $inputWrap = $('<div class="colorPreviewInputWrap"></div>');
      var $previewWrap = $('<div class="colorPreviewWrap"></div>');
      var $preview = $('<div class="colorPreview"></div>');
      $previewWrap.append($preview);
      $dom.data('$preview', $preview);
      $dom.addClass('colorPreviewed');
      $dom.wrap($inputWrap);
      $dom.after($previewWrap);
    },
    backend: function () {},
    frontend: function ($dom, keyobj, event) {
      const value = this.value(keyobj) || $dom.attr('placeholder');
      const $preview: JQuery = $dom.data('$preview');
      if (!$preview) {
        // Shouldn't happen
        return;
      }
      if (typeof value !== 'undefined') {
        $preview.css('background', value);
      } else {
        $preview.css('background', 'transparent');
      }
    },
  },

  linthost: {
    backend: function ($dom, keyobj, event) {
      if (event.type !== 'change') return;

      // Filter the value based on certain rules
      var val = $dom.val().toString();
      val = val.toLowerCase();
      val = val.replace(/ /g, '-').replace(/[^a-zA-Z0-9_-]/g, '');

      // Only update data if the value is changed
      if (this.data(keyobj.param) !== val) {
        this.data(keyobj.param, val);
      }
    },
    frontend: function ($dom, keyobj, event, smooth) {
      // Don't execute changes on keypress etc
      if (event.type && event.type !== 'change') return;
      var val = this.data(keyobj.param) || '';
      // Don't set the value if it's the same as before
      if (val === $dom.val()) return;
      setValNoFocus($dom, val.toString());
      $dom.trigger('hostChange');
    },
  },

  lintpath: {
    backend: function ($dom, keyobj, event) {
      if (event.type !== 'change') return;

      // Filter the value based on certain rules
      let val = $dom.val().toString();
      val = val.toLowerCase();
      val = val
        .replace(/ /g, '-')
        .replace(/æ/g, 'ae')
        .replace(/ø/g, 'oe')
        .replace(/å/g, 'aa')
        .replace(/ä/g, 'a')
        .replace(/ö/g, 'o')
        .replace(/[^a-zA-Z0-9_-]/g, '');

      // Check if parent has children with the same key
      let that = this;

      function unique(val: string, conflictIterator?: number) {
        let match = val;
        if (conflictIterator) {
          match += '-' + conflictIterator;
        }
        if (that.parent.related[that.parentParam]) {
          let relationList = that.parent.related[that.parentParam];
          for (let relation of relationList) {
            let child = relation.element;
            if (that === child) {
              continue; // Don't compare to self
            }
            if (child.data(keyobj.param) === match) {
              match = unique(val, (conflictIterator || 0) + 1);
              break;
            }
          }
        }
        return match;
      }
      val = unique(val);

      // Only update data if the value is changed
      if (this.data(keyobj.param) !== val) {
        this.data(keyobj.param, val);
        this.property('wantedKey', val);
      }
    },
    frontend: function ($dom, keyobj, event, smooth) {
      // Don't execute changes on keypress etc
      if (event.type && event.type !== 'change') return;
      var val = this.data(keyobj.param) || '';
      // Don't set the value if it's the same as before
      if (val === $dom.val()) return;
      setValNoFocus($dom, val);
    },
  },

  hostname: {
    init: function ($dom, keyobj) {
      $dom.css('background', 'transparent');
    },
    backend: function ($dom, keyobj, event) {
      if (event.type !== 'change') return;

      // Filter the value based on certain rules
      var val = $dom.val().toString();
      val = val.toLowerCase();

      // Only update data if the value is changed
      if (this.data(keyobj.param) !== val) {
        this.data(keyobj.param, val);
        this.parent.submit('_id', this.parent);
        this.parent.submit('entity', this.parent.entity);
        //this.parent.submit('updatetypekit.exec', true); // This is now done in PeekyHostname
      }
    },
    frontend: function ($dom, keyobj) {
      var hostname: string = this.data('hostname'),
        wantedHostname: string = this.data('wantedHostname'),
        validate,
        unavailable = /.*peeky\.(com|net|org|no)$/;

      function hostalert(ok, message) {
        var $parent = $dom.parent(),
          $icon = $parent.find('.warningIcon'),
          $message = $parent.find('.warningMessage');
        if (ok) $icon.removeClass('sprite').removeClass('_warning');
        else $icon.addClass('sprite').addClass('_warning');
        $message.html(message);
        if (!ok) $dom.css('color', '#DC2919');
        else $dom.css('color', '');
      }

      // Site is not upgraded
      if (!(this.parent.data('sitePlan') >= 100)) {
        hostalert(
          false,
          'You need to upgrade the site to a paid plan in order to activate a custom host name.'
        );
        setValNoFocus($dom, hostname || wantedHostname);
      }

      // Host name already assigned
      else if (!hostname && wantedHostname) {
        setValNoFocus($dom, wantedHostname);
        hostalert(
          false,
          'The host name <strong>' +
            wantedHostname +
            '</strong> is assigned to another Peeky site.'
        );
      } else if (!hostname) {
        hostalert(true, '');
      } else if (unavailable.test(hostname)) {
        hostalert(
          false,
          'The host name <strong>' + hostname + '</strong> is not allowed.'
        );
        setValNoFocus($dom, hostname);
      }
      // Host name not assigned
      else {
        validate =
          /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/;
        // Host name is invalid
        if (!hostname || !validate.test(hostname)) {
          hostalert(false, 'The host name is invalid.');
        } else {
          $.ajax('/checkhostname/' + hostname + '/').done(function (data) {
            if (!data || !data.m.status) {
              // Error getting status
              hostalert(
                false,
                'Could not check host name status (unknown error).'
              );
              return;
            } else if (data.m.status.OK_HOSTNAME) {
              hostalert(true, 'The host name is working correctly');
            } else if (data.m.status.ER_HOSTNAME_NOTFOUND) {
              // No IP address found
              hostalert(
                false,
                'We were unable to look up the host name <strong>' +
                  hostname +
                  '</strong>. Is it set up correctly?'
              );
            } else if (data.m.status.ER_HOSTNAME_WRONGADDRESS) {
              // Wrong address assigned
              hostalert(
                false,
                'The host name is currently not pointing to Peeky.'
              );
            } else {
            }
          });
        }

        setValNoFocus($dom, hostname);
      }
    },
  },

  peekyhostname: {
    frontend: function ($dom, keyobj, e) {
      let key = this.data('key') || '';
      let wantedKey = this.data('wantedKey') || '';

      // This is a keyboard-triggered event, don't check before server returns
      if (e.originalEvent) {
        return;
      }

      function hostalert(ok, message) {
        let $parent = $dom.closest('.warnable');
        let $icon = $parent.find('.warningIcon');
        let $message = $parent.find('.warningMessage');
        if (ok) {
          $icon.removeClass('sprite').removeClass('_warning');
        } else {
          $icon.addClass('sprite').addClass('_warning');
        }
        $message.html(message);
      }

      if (wantedKey && key.toLowerCase() !== wantedKey.toLowerCase()) {
        $dom.css('color', '#DC2919');
        hostalert(false, 'The host name is not available');
      } else {
        $dom.css('color', '');
        hostalert(true, '');
      }
      setValNoFocus($dom, wantedKey || key);
      $dom.trigger('hostChange');
    },
  },

  collaborator: {
    init: function ($dom, keyobj) {
      var that = this;
      $dom.on('keyup', function (e) {
        if (e.which === 13) {
          var email = $dom.val().toString();
          if (/[^@]+@[^\.]+\..+/.test(email)) {
            that.relate(
              '_collaborator',
              false,
              {
                entity: 'user',
                data: {
                  email: email,
                  _maybeRegister: '1',
                  _collabSite: that.data('_id'),
                },
              },
              true
            );
            setTimeout(function () {
              that.updateUI('related._collaborator', true);
              $dom.val('');
            });
          } else {
          }
        }
      });
    },
    backend: function ($dom, keyobj) {},
    frontend: function ($dom, keyobj) {},
  },

  striphtml: {
    init: function ($dom, keyobj) {},
    backend: function ($dom, keyobj) {},
    frontend: function ($dom, keyobj) {
      var value = this.value(keyobj);
      if (typeof value === 'undefined') {
        value = '';
      }
      value = '' + value;
      value = value.replace(/<[^>]*>/g, '');
      $dom.text(value);
    },
  },

  attr: {
    init: function ($dom, keyobj) {},
    backend: function ($dom, keyobj) {},
    frontend: function ($dom, keyobj) {
      var that = this;
      var attributes = $dom.data('attributes');
      for (let attribute in attributes) {
        var template = attributes[attribute] || '';
        var val = template.replace(/{([^}]+)}/g, function (match, capture) {
          var split = capture.split(/\|/);
          for (let i = 0; i < split.length && !value; i++) {
            var consider = split[i];
            var value;
            if (consider.charAt(0) === '"' || consider.charAt(0) === "'") {
              value = consider.slice(1).slice(0, -1); // Remove quotes
            } else {
              value = that.value(consider);
            }
          }
          return value;
        });
        $dom.attr(attribute, val);
      }
    },
  },

  hashmap: {
    init: function ($dom, keyobj) {
      /*
      // build html structure
      var that = this;
      var param = keyobj.param;
      var properties;
      try {
        properties = JSON.parse(that.property(param)) || {};
        if (typeof(properties) !== 'object') properties = {};
      } catch(e) {
        properties = {};
      }
      function add(key, value) {
        if (typeof(key) === 'undefined') key = '';
        if (typeof(value) === 'undefined') value = '';
        var $wrapper = $('<div class="hashmapEntry"></div>');
        var $key = setValNoFocus($('<input type="text" class="text hashmapKey" placeholder="source">'), key);
        var $value = setValNoFocus($('<input type="text" class="text hashmapValue" placeholder="destination">'), value);
        var $remove = $('<span class="clickable remove">×</span>');
        var $all = $key.add($value).add($remove);
        $all.data('key', key);
        $all.data('$all', $all);
        $wrapper.append($remove);
        $wrapper.append($key);
        $wrapper.append('<span>→</span>');
        $wrapper.append($value);
        $dom.append($wrapper);
        $remove.on('click', function() {
          delete(properties[$key.data('key')]);
          that.property(keyobj.param, JSON.stringify(properties));
          $wrapper.slideUp(200, function() {
            $wrapper.remove();
          });
        });
      };
      var $add = $('<div class="hashmapEntry clickable textListHeader"><div class="hashmapAdd">Add path alias</div></div>');
      $dom.append($add);
      for (let k in properties) {
        add(k, properties[k]);
      }
      $dom.on('change', '.hashmapKey', function() {
        var oldkey = $(this).data('key');
        var newkey = $(this).val();
        var $all = $(this).data('$all');
        $all.data('key', newkey);
        properties[newkey] = properties[oldkey];
        delete(properties[oldkey]);
        that.property(keyobj.param, JSON.stringify(properties));
      });
      $dom.on('change', '.hashmapValue', function() {
        var key = $(this).data('key');
        var val = $(this).val();
        var $all = $(this).data('$all');
        properties[key] = val;
        that.property(keyobj.param, JSON.stringify(properties));
      });
      $dom.on('click', '.hashmapAdd', function() {
        add(undefined, '');
      });
      */
    },
    frontend: function ($dom, keyobj, event, smooth) {},
    backend: function ($dom, keyobj, event, smooth) {},
  },

  isActive: {
    init: function ($dom) {},
    frontend: function ($dom, keyobj, event, smooth) {
      var showActive =
        $dom.data('showactive') || $dom.data('default') || 'published';
      var value = this.value(keyobj) || false;
      var type = typeof value;
      var show = false;

      if (showActive === 'published') {
        if (
          (type === 'boolean' && value) ||
          value === 'true' ||
          new Date(value) < new Date()
        ) {
          show = true;
        }
      } else if (showActive === 'unpublished') {
        if (
          (type === 'boolean' && !value) ||
          value === 'false' ||
          new Date(value) >= new Date(2038, 0, 19)
        ) {
          // Unpublished articles are set to this date
          show = true;
        }
      } else if (showActive === 'scheduled') {
        if (type !== 'boolean' && new Date(value) >= new Date()) {
          show = true;
        }
      }

      if (show) {
        if (smooth && $dom.is(':visible')) $dom.stop().slideDown(100);
        else $dom.css('display', '');
      } else {
        if (smooth && $dom.is(':visible')) $dom.stop().slideUp(100);
        else $dom.css('display', 'none');
      }
    },
  },

  submitValue: {
    init: function ($dom, keyobj, event) {},
    frontend: function ($dom, keyobj, event) {},
    backend: function ($dom, keyobj, event) {
      let values = $dom.data('value');

      if (typeof values === 'undefined') {
        return;
      }

      if (typeof values !== 'object') {
        let newValue = {};
        newValue[keyobj.unique] = values;
        values = newValue;
      }

      if (typeof values !== 'object') {
        return;
      }

      for (let k in values) {
        let value = values[k].toString();
        let dotIndex = k.indexOf('.');
        let type;
        let param;
        if (dotIndex >= 0) {
          type = k.substr(0, dotIndex);
          param = k.substr(dotIndex + 1);
        } else {
          type = 'insert';
          param = k;
        }

        if (type === 'insert') {
          this.data(param, values[k], true, $dom.data('reload'));
        } else if (type === 'property') {
          this.property(param, values[k], true, $dom.data('reload'));
        }
      }
    },
  },

  hide: {
    init: function ($dom) {},
    frontend: function ($dom: JQuery, keyobj, event, smooth) {
      let showstring = $dom.data('showstring');
      let hidestring = $dom.data('hidestring');
      let visible: boolean;
      let valueIsUndefined = false;
      let value = this.value(keyobj) || $dom.data('default');

      let disablehide = $dom.data('disablehide');
      if (disablehide) {
        return;
      }

      if (typeof value !== 'undefined') {
        valueIsUndefined;
        value = value + '';
      }

      if (typeof showstring !== 'undefined') {
        if (!Array.isArray(showstring)) {
          showstring = (showstring + '').split(',');
        }
      }
      if (typeof hidestring !== 'undefined') {
        if (!Array.isArray(hidestring)) {
          hidestring = (hidestring + '').split(',');
        }
      }

      // if showstring
      //  remove everything except true
      if (showstring && !hidestring) {
        //visible = (showstring == value);
        visible = showstring.indexOf(value) >= 0;
      }
      // if hidestring
      //  remove only hidestring
      else if (!showstring && hidestring) {
        //visible = (value != hidestring);
        visible = hidestring.indexOf(value) < 0;
      } else if ((hidestring || ['false']).indexOf(value) >= 0) {
        //else if (value === (hidestring || 'false')) {
        visible = false;
      } else if ((showstring || ['true']).indexOf(value) >= 0) {
        //else if (value === (showstring || 'true')) {
        visible = true;
      } else if (valueIsUndefined) {
        visible =
          $dom.data('hidedefault') !== 'true' ||
          $dom.data('hidedefault') !== true;
      } else {
        visible = !!value;
      }

      if ($dom.data('hideinvert')) {
        visible = !visible;
      }

      //let isCurrentlyVisible = $dom.css('display') !== 'none';
      let isCurrentlyVisible = $dom[0].style.display !== 'none';
      let changed = visible !== isCurrentlyVisible;

      if (!changed) {
        return;
      }

      if (visible) {
        if (smooth) $dom.fadeIn(100);
        else $dom.css('display', '');
      } else {
        if (smooth) $dom.fadeOut(100);
        else $dom.css('display', 'none');
      }

      var doc = $dom[0].ownerDocument;
      var win = doc.defaultView;
      var ipk = win.pk;
      if (ipk) {
        this.editor.queueTrigger('resize', ipk);
      }
    },
    backend: function () {},
  },

  opacity: {
    init: function ($dom) {},
    frontend: function ($dom, keyobj, event, smooth) {
      var val = this.property(keyobj.param);
      if (typeof val === 'undefined') val = 100;
      if ($dom.css('opacity') === val) return;
      $dom.css('opacity', val / 100);
    },
    backend: function () {},
  },

  toggleClass: {
    init: function ($dom, keyobj) {},
    frontend: function ($dom, keyobj, event, smooth) {
      var classes = $dom.data('classes') || '';
      var value;
      var toggled = false;
      var value = this.value(keyobj);

      if (typeof value === 'undefined') {
        return;
      }

      value = value.toString();

      if (typeof classes === 'object') {
        classes =
          classes[keyobj.type + '.' + keyobj.param] || classes[keyobj.param];
      }
      if (!classes) {
        return;
      }
      if (typeof classes === 'string') {
        classes = classes.split(',');
      } else if (Array.isArray(classes)) {
      } else if (typeof classes === 'object') {
        var newClasses = [];
        $.each(Object.keys(classes), function (i, val) {
          if (typeof classes[val] === 'object') {
            newClasses.push(classes[val]);
          } else {
            newClasses.push({ value: val, className: classes[val] });
          }
        });
        classes = newClasses;
      }

      //
      $dom.addClass('preToggleClass');

      for (let i = 0; i < classes.length; i++) {
        let classValue: string;
        let className: string;
        if (typeof classes[i] === 'object') {
          classValue = classes[i].value;
          className = classes[i].className;
        } else {
          classValue = classes[i];
          className = classes[i];
        }
        if (value === classValue && !$dom.is('.' + className)) {
          $dom.addClass(className);
          toggled = true;
        } else if (value !== classValue && $dom.is('.' + className)) {
          $dom.removeClass(className);
          toggled = true;
        } else {
        }
      }

      //
      $dom.removeClass('preToggleClass');

      if (toggled) {
        $dom.addClass('postToggleClass');
        setTimeout(function () {
          $dom.removeClass('postToggleClass');
        });
        $dom.trigger('toggleClass');
        var doc = $dom[0].ownerDocument;
        var win = doc.defaultView;
        var ipk = win.pk;
        if (ipk) {
          this.editor.queueTrigger('toggleClass', ipk);
          this.editor.queueTrigger('resize', ipk);
        }
      }
    },
    backend: function () {},
  },

  toggleValue: {
    init: function ($dom, keyobj) {
      var values = ($dom.data('values') || '').split(',');
      var that = this;
      $dom.on($dom.data('toggleon') || 'click', function () {
        var value = ($dom.data('value') || '') + '';
        var currentIndex;
        for (currentIndex = 0; currentIndex < values.length; currentIndex++) {
          if (value == values[currentIndex]) {
            break;
          }
        }
        var nextIndex = (currentIndex + 1) % values.length;
        value = values[nextIndex];
        if (keyobj.type === 'insert') {
          that.data(keyobj.param, value, true, $dom.data('reload'));
        } else if (keyobj.type === 'property') {
          that.property(keyobj.param, value, true, $dom.data('reload'));
        } else {
          return;
        }
        $dom.data('value', value);
        that.updateUI(keyobj);
        $(this).trigger('toggleValue', { value: value });
      });
    },
    frontend: function ($dom, keyobj, event, smooth) {
      var value = this.value(keyobj);
      // If value in database is undefined, don't overwrite default value
      if (typeof value !== 'undefined') {
        $dom.data('value', value);
      }
    },
    backend: function () {},
  },

  exclude: {
    init: function ($dom) {},
    frontend: function ($dom, keyobj, event, smooth) {
      let $existing = $dom.children();
      let related = this.related[keyobj.param] || [];
      let index = {};

      if ($existing.length <= 0) {
        return;
      }

      // Create index of items to remove
      for (let i = related.length - 1; i >= 0; i--) {
        let relation = related[i];
        index[relation.element.key] = true;
      }

      // Loop items in list to show/hide
      $existing.each(function () {
        let $item = $(this);
        if (index[$item.data('element').key]) {
          if (smooth) {
            $item.stop().slideUp(150);
          } else {
            $item.stop().hide();
          }
          $item.addClass('excluded');
        } else {
          if (smooth) {
            $item.stop().slideDown(150);
          } else {
            $item.stop().show();
          }
          $item.removeClass('excluded');
        }
      });
    },
  },

  // Drag/drop sorting
  sort: {
    init: function ($dom, keyobj) {
      var containerElement = this;

      $dom.addClass('sortable');

      $dom.on(
        'mousedown',
        'input, textarea, .listItem .listItem',
        function (e) {
          e.stopPropagation();
        }
      );
      $dom.on('mousedown', '.listItem,.textListItem', function (e) {
        // Only drag with left-mouse button
        if (e.which !== 1 && e.which !== undefined) return;
        e.preventDefault();
        e.stopPropagation();

        var that = this,
          moved = false,
          finished = false,
          $body = $dom.closest('body'),
          startX = e.pageX,
          startY = e.pageY,
          startPos,
          previousMoveEvent,
          $element = $(this),
          $scrollParent = scrollParent($element),
          scrollParentOffset = $scrollParent.offset(),
          //, scrollParentWidth = $scrollParent.width()
          scrollParentHeight = $scrollParent.height(),
          scrollParentScrollHeight = $scrollParent.prop('scrollHeight'),
          scrollParentScrollTop = $scrollParent.scrollTop(),
          scrollParentScrollLeft = $scrollParent.scrollLeft(),
          scrollOffsetTop = 0,
          scrollOffsetLeft = 0,
          element: EditorElement = $element.data('element'),
          elementHeight = $element.height(),
          //, elementWidth = $element.width()
          parentOffset = $element.offsetParent().offset(),
          $clone: JQuery,
          siblings = [],
          $replace = $element,
          currentPos,
          $window: JQuery<Window | Window[]>;
        var scrollInterval;
        var scrollSpeed = 0;

        function atTop() {
          return scrollParentScrollTop <= 0;
        }

        function atBottom() {
          return (
            scrollParentScrollTop + scrollParentHeight >=
            scrollParentScrollHeight
          );
        }

        function onScroll() {
          var diffY = scrollParentScrollTop - $scrollParent.scrollTop();
          var diffX = scrollParentScrollLeft - $scrollParent.scrollLeft();
          scrollParentScrollTop = $scrollParent.scrollTop();
          scrollParentScrollLeft = $scrollParent.scrollLeft();
          scrollOffsetTop -= diffY;
          scrollOffsetLeft -= diffX;
          mousemove();
        }

        function scroll(reverse) {
          var scrollTo;
          if (reverse) {
            if (scrollParentScrollTop > 0) {
              scrollTo = Math.max(scrollParentScrollTop - elementHeight, 0);
            }
          } else {
            if (
              scrollParentScrollTop + scrollParentHeight <
              scrollParentScrollHeight
            ) {
              scrollTo = Math.min(
                scrollParentScrollTop + elementHeight,
                scrollParentScrollHeight - scrollParentHeight
              );
            }
          }
          if (typeof scrollTo !== 'undefined') {
            $scrollParent.scrollTop(scrollTo);
          }
        }
        function setScroll(speed) {
          if (scrollSpeed === speed) return;
          scrollSpeed = speed;
          if (scrollInterval) {
            scrollInterval = clearInterval(scrollInterval);
          }
          if (!speed) {
            scrollSpeed = 0;
          } else {
            scroll(speed < 0);
            scrollInterval = setInterval(function () {
              if (atTop() || atBottom()) {
                scrollInterval = clearInterval(scrollInterval);
              } else {
                scroll(speed < 0);
              }
            }, Math.abs(2500 / speed));
          }
        }

        function mousemove(e?) {
          if (!e) {
            e = previousMoveEvent;
          } else {
            previousMoveEvent = e;
          }
          if (!e) {
            return;
          }
          e.preventDefault();

          if (
            !moved &&
            Math.abs(e.pageX - startX + scrollOffsetLeft) +
              Math.abs(e.pageY - startY + scrollOffsetTop) >
              5
          )
            moved = true;
          if (!moved) return;

          function updateSiblings() {
            siblings = [];
            $element
              .parent()
              .children('.listItem:not(._clone),.textListItem:not(._clone)')
              .each(function () {
                var $this = $(this),
                  offset = $this.offset();
                siblings.push({
                  top: offset.top + scrollOffsetTop,
                  right: offset.left + $this.outerWidth() + scrollOffsetLeft,
                  bottom: offset.top + $this.outerHeight() + scrollOffsetTop,
                  left: offset.left + scrollOffsetLeft,
                  index: siblings.length,
                  $: $this,
                });
              });
          }

          function checkScrollBoundaries() {
            var offTop = e.pageY - scrollParentOffset.top - elementHeight;
            //var offLeft = (e.pageX-scrollParentOffset.left) - elementCursorOffsetX;
            var offBottom =
              scrollParentHeight -
              (e.pageY - scrollParentOffset.top) -
              elementHeight;
            //var offRight = scrollParentWidth - (e.pageX-scrollParentOffset.left) - (elementWidth-elementCursorOffsetX);
            if (offTop < -elementHeight * 0.8) setScroll(-80);
            else if (offTop < -(elementHeight / 2)) setScroll(-20);
            else if (offTop < 0) setScroll(-1);
            else if (offBottom < -elementHeight * 0.8) setScroll(80);
            else if (offBottom < -(elementHeight / 2)) setScroll(20);
            else if (offBottom < 0) setScroll(10);
            else setScroll(0);
          }
          checkScrollBoundaries();

          if (!$clone) {
            // Create draggable dummy
            $clone = $element
              .clone()
              .css({
                position: 'absolute',
                width: $element.width(),
                zIndex: 10,
              })
              .addClass('_clone')
              .addClass('hover')
              .removeClass('pressed');
            $clone.on('selectstart', false);
            $element.after($clone);
            $element.css('visibility', 'hidden');

            startPos = $element.position();
            updateSiblings();
          }

          // Switch "active" item
          $.each(siblings, function (index, s) {
            var ex = e.pageX + scrollOffsetLeft;
            var ey = e.pageY + +scrollOffsetTop;
            if (s.left < ex && ex < s.right && s.top < ey && ey < s.bottom) {
              if (typeof currentPos === 'undefined') currentPos = index;
              else if (index !== currentPos) {
                $replace = s.$;
                $element.detach();
                if (index > currentPos) $replace.after($element);
                else $replace.before($element);
                updateSiblings();
                currentPos = index;
              }
              return false;
            }
          });

          // Position
          $clone.css({
            top: startPos.top + (e.pageY - startY + scrollOffsetTop),
            //,left: startPos.left + (e.pageX-startX+scrollOffsetLeft)
          });

          return false;
        }

        $scrollParent.on('scroll', onScroll);
        $body.on('mousemove', mousemove);

        if ($('#bodyframe').length > 0) {
          let $iframe: JQuery<HTMLIFrameElement> = $('iframe#bodyframe');
          let iframe = $iframe[0];
          $window = $([iframe.contentWindow, window]);
        } else {
          $window = $(window);
        }
        $window.one('mouseup', function (e) {
          if (finished) {
            return;
          }
          finished = true;

          $scrollParent.off('scroll', onScroll);
          $body.off('mousemove', mousemove);

          if (scrollInterval) {
            scrollInterval = clearInterval(scrollInterval);
          }

          if (!moved) {
            if ($clone) $clone.remove();
            $element.css('visibility', '');
            return;
          }

          if ($clone) {
            $clone.css({
              top: $clone.position().top,
              left: $clone.position().left,
            });
            $clone.animate(
              {
                top: $element.offset().top - parentOffset.top + scrollOffsetTop,
                left:
                  $element.offset().left - parentOffset.left + scrollOffsetLeft,
              },
              100,
              function () {
                $element.css('visibility', '');
                $clone.hide();
                setTimeout(function () {
                  $clone.remove();
                  // Update back-end
                  containerElement.updateBackend(
                    keyobj.unique + ':' + keyobj.func,
                    $dom
                  );
                  containerElement.updateUI(keyobj.unique, e, true);
                });
              }
            );

            //
            if (element !== $replace.data('element')) {
              let targetElement = $replace.data('element');
              element.submit('entity', element.entity);
              element.submit('_id', element);
              element.submit('move.target.key', targetElement.key);
              element.submit('move.relation.root.key', containerElement.key);
              element.submit(
                'move.relation.param',
                keyobj.param,
                $dom.data('reload'),
                true
              );
              element.submit(
                containerElement,
                'entity',
                containerElement.entity
              );
              element.submit(containerElement, '_id', containerElement);
              element.submit(targetElement, 'entity', targetElement.entity);
              element.submit(targetElement, '_id', targetElement);
            }

            // Make other events waiting for body.mouseUp get released
            setTimeout(function () {
              $element.trigger('mouseup');
            });
          }
          return false;
        });
      });
    },
    backend: function ($dom, keyobj, event) {
      let parameter = keyobj.param;
      let newlist: EditorElement[] = [];
      let relationList = this.related[parameter] || [];
      let relationListClone = relationList.slice();

      // Update this.children order
      $dom.children('.listItem,.textListItem').each(function (index) {
        let element: EditorElement = $(this).data('element');
        newlist.push(element); // Unshift instead of push, since new elements are at the top
      });

      // Populate oldList from relations
      for (let i = 0; i < relationListClone.length; i++) {
        let relation = relationListClone[i];
        let oldElement = relation.element;
        // Get new position for element
        let newIndex = newlist.indexOf(oldElement);
        // If new position isn't the same as before, move it
        if (newIndex !== i && newIndex >= 0) {
          relationList[newIndex] = relation;
        }
      }
    },
    frontend: function ($dom, keyobj, event, smooth) {},
  },

  gallerySort: {
    init: function () {},
    backend: function () {},
    frontend: function ($dom, keyobj) {
      let gallery = $dom.data('gallery');
      let images = gallery && gallery.imageset ? gallery.imageset.images : [];
      let param = keyobj.param;
      let relationList = this.related[param] || [];
      let newImages = [];
      let i, l;
      let index = {};

      if (!gallery) return;
      if (!images) return;
      if (!gallery.imageset) return;

      // Generate index
      for (let i = 0; i < images.length; i++) {
        index[images[i].data._id] = images[i];
      }

      // Update this.children order
      for (let i = 0; i < relationList.length; i++) {
        let relation = relationList[i];
        let element = relation.element;
        if (index[element.data('_id')]) {
          newImages.push(index[element.data('_id')]);
        }
      }

      gallery.imageset.images = newImages;

      gallery.triggerAction('updateClickers');
    },
  },

  gallery: {
    init: function ($dom, keyobj, event) {},
    frontend: function ($dom, keyobj) {
      var that = this;
      var gallery = $dom.data('gallery');
      var _id = this.data('_id');

      if (!gallery) return;
      if (!gallery.imageset) return;
      if (!gallery.imageset.images) return;

      var value = this.property(keyobj.param);

      function updateImageProperty() {
        if (typeof value === 'undefined') value = '';
        for (let i = 0, l = gallery.imageset.images.length; i < l; i++) {
          if (gallery.imageset.images[i].data._id.toString() !== _id.toString())
            continue;
          if (gallery.imageset.images[i].data[keyobj.param] !== value) {
            gallery.imageset.images[i].data[keyobj.param] = value;
            if (gallery.currentImage === gallery.imageset.images[i]) {
              gallery.triggerAction('render');
            }
          }
          break;
        }
      }

      if (keyobj.param === 'bgColor') {
        for (let i = 0, l = gallery.imageset.images.length; i < l; i++) {
          if (gallery.imageset.images[i].data._id.toString() !== _id.toString())
            continue;

          gallery.imageset.images[i].data.bgColor = this.property('bgColor');
          //gallery.imageset.images[i].$bgColor = $.Color(gallery.imageset.images[i].data.bgColor);
          if (gallery.currentImage === gallery.imageset.images[i]) {
            gallery.triggerAction('render');
          }
          break;
        }
      } else if (keyobj.param === 'type') {
        for (let i = 0, l = gallery.imageset.images.length; i < l; i++) {
          if (gallery.imageset.images[i].data._id.toString() !== _id.toString())
            continue;
          if (gallery.imageset.images[i].data.type !== this.property('type')) {
            gallery.imageset.images[i].data.type = this.property('type');
            if (gallery.currentImage === gallery.imageset.images[i]) {
              gallery.triggerAction('render');
            }
          }
        }
      } else if (keyobj.param === 'upscale') {
        if (value === 'yes') value = true;
        else value = false;
        for (let i = 0, l = gallery.imageset.images.length; i < l; i++) {
          if (gallery.imageset.images[i].data._id.toString() !== _id.toString())
            continue;
          if (gallery.imageset.images[i].data.upscale !== value) {
            gallery.imageset.images[i].data.upscale = value;
            if (gallery.currentImage === gallery.imageset.images[i]) {
              gallery.triggerAction('render');
            }
          }
        }
      } else if (keyobj.param === 'slowzoom') {
        if (value === 'yes') value = true;
        else value = false;
        for (let i = 0, l = gallery.imageset.images.length; i < l; i++) {
          if (gallery.imageset.images[i].data._id.toString() !== _id.toString())
            continue;
          if (!value && gallery.imageset.images[i].data.animation) {
            gallery.imageset.images[i].data.animation = undefined;
            if (gallery.currentImage === gallery.imageset.images[i]) {
              gallery.triggerAction('animate', {
                transition: { scaleInTo: 1 },
                duration: 500,
              });
            }
          } else if (value && !gallery.imageset.images[i].data.animation) {
            gallery.imageset.images[i].animation = {
              duration: 15000,
              transition: {
                scaleInTo: 1.2,
              },
            };
            if (gallery.currentImage === gallery.imageset.images[i]) {
              gallery.triggerAction('animate', {
                transition: { scaleInTo: 1.2 },
                duration: 15000,
              });
            }
          }
        }
      } else if (keyobj.param === 'loopvideo') {
        if (value === 'yes') value = true;
        else value = false;
        updateImageProperty();
      } else if (keyobj.param === 'autoplay') {
        if (value === 'yes') value = true;
        else value = false;
        updateImageProperty();
      } else if (keyobj.param === 'uiColor') {
        updateImageProperty();
      } else if (keyobj.param === 'ui_playPause') {
        if (value === 'false') value = false;
        else value = true;
        updateImageProperty();
      } else if (keyobj.param === 'ui_progress') {
        if (value === 'false') value = false;
        else value = true;
        updateImageProperty();
      } else if (keyobj.param === 'ui_volume') {
        if (value === 'false') value = false;
        else value = true;
        updateImageProperty();
      } else if (keyobj.param === 'ui_fullscreen') {
        if (value === 'false') value = false;
        else value = true;
        updateImageProperty();
      } else if (keyobj.param === 'loopGallery') {
        var loop = this.property('loopGallery');
        if (!loop || loop === 'false') loop = false;
        else loop = true;
        if (gallery.imageset.loop !== loop) {
          gallery.imageset.loop = loop;
          gallery.triggerAction('updateClickers');
        }
      } else if (keyobj.param === 'animation') {
        var animation = this.property('animation'),
          slideIn,
          slideOut,
          fadeIn,
          fadeOut,
          slide,
          fade,
          scaleInFrom,
          scaleInTo,
          scaleOutFrom,
          scaleOutTo;
        if (animation === 'shuffle') {
          slideIn = true;
          fadeOut = true;
        } else if (animation === 'fade') {
          fade = true;
        } else if (animation === 'zoom') {
          fade = true;
          scaleInFrom = 0.7;
          scaleOutTo = 1.2;
        } else {
          slide = true;
        }
        if (!gallery.imageset || !gallery.imageset.transition) {
          return;
        }
        gallery.imageset.transition.slide = slide;
        gallery.imageset.transition.slideIn = slideIn;
        gallery.imageset.transition.slideOut = slideOut;
        gallery.imageset.transition.fade = fade;
        gallery.imageset.transition.fadeIn = fadeIn;
        gallery.imageset.transition.fadeOut = fadeOut;
        gallery.imageset.transition.scaleInFrom = scaleInFrom;
        gallery.imageset.transition.scaleInTo = scaleInTo;
        gallery.imageset.transition.scaleOutFrom = scaleOutFrom;
        gallery.imageset.transition.scaleOutTo = scaleOutTo;
      } else if (keyobj.param === 'animDuration') {
        var duration = this.property('animDuration');
        if (typeof duration === 'undefined') return;
        if (!duration && duration !== 0) duration = 500;
        duration = parseInt(duration || 0, 10);
        if (gallery.imageset.transition.duration !== duration) {
          gallery.imageset.transition.duration = duration;
        }
      } else if (keyobj.param === 'autocycle') {
        var duration = this.property('autocycle');
        if (typeof duration === 'undefined') return;
        if (gallery._autocycleVal === duration) return;
        gallery._autocycleVal = duration;
        // Remove previous pause-timeout
        if (gallery._autocycleUiTimeout) {
          clearTimeout(gallery._autocycleUiTimeout);
          gallery._autocycleUiTimeout = undefined;
        }
        // Stop the animation after sliding 4 times
        if (duration > 0) {
          gallery._autocycleUiTimeout = setTimeout(function () {
            gallery.currentData.autocycle = undefined;
            gallery.triggerAction('updateAutocycle');
            gallery.triggerAction('show', { no: 0 });
          }, Math.max(2000, duration * 4 + 1));
        }
        gallery.currentData.autocycle = duration;
        if (gallery.imageNo() != 0) {
          gallery.triggerAction('show', { no: 0 });
          gallery.triggerAction('updateAutocycle');
        } else {
          gallery.triggerAction('updateAutocycle');
          gallery.triggerAction('startAutocycle');
        }
      }
    },
    backend: function () {},
  },

  activateForm: {
    init: function ($dom, keyobj) {
      var that = this;
      $dom.on('click' + ' immediateClick', function (e) {
        if ($(e.target).is('.delete,.settingsDrop')) {
          return;
        }
        if (that._active) {
          that._active = false;
        } else {
          that._active = true;
        }
        that.updateUI(keyobj.fullkey, e, true);
      });
      $dom.on('mouseenter', function (e) {
        that._hover = true;
        that.updateUI(keyobj.fullkey, e, true);
      });
      $dom.on('mouseleave', function (e) {
        that._hover = false;
        that.updateUI(keyobj.fullkey, e, true);
      });
    },
    frontend: function () {},
    backend: function () {},
  },

  activateTextarea: {
    init: function ($dom, keyobj) {
      var that = this,
        updating = false;

      function update() {
        if (updating) return;
        updating = true;
        setTimeout(function () {
          updating = false;
          that.updateUI(keyobj.fullkey);
        }, 20);
      }

      if ($dom.is('textarea')) {
        $dom.on('blur', function () {
          that._textFocus = false;
          update();
        });
        $dom.on('focus', function () {
          that._textFocus = true;
          update();
        });
      }
    },
    frontend: function ($dom) {
      if (!$dom.is('textarea')) {
        if (
          (this._textFocus && !$dom.is('.open')) ||
          (!this._textFocus && $dom.is('.open'))
        ) {
          $dom.find('.openClose').trigger('click', true);
        }
      }
    },
    backend: function () {},
  },

  activateGallery: {
    init: function ($dom, keyobj) {
      var that = this,
        gallery = $dom.data('gallery');

      if (!gallery || $dom.data('galleryBound') || !gallery.on) return;
      $dom.data('galleryBound', true);

      gallery.on('initSwitch', function (param) {
        var image = gallery.imageset.images[param.no];
        if (!image) return;
        var element = that.idIndex(image.data._id);

        if (gallery._feActiveId === image.data._id) return;
        gallery._feActiveId = image.data._id;

        if (typeof element.catcher$dom['activate.link'] === 'object') {
          $.each(element.catcher$dom['activate.link'], function (index, item) {
            var $item = item.$dom;
            if ($item.is('.listHeader')) {
              setTimeout(function () {
                if ($item.closest('.listItem').is('.isExtended')) return;
                if (!$item.closest('.group').is('.dropped')) return;
                $item.trigger('click');
              }, 0);
            }
          });
        }
      });

      gallery.on('activate', function () {
        this._feActiveId = undefined;
      });

      gallery.$container.on('dblclick', function () {
        var image = gallery.currentImage,
          _id = image ? image.data._id : -1,
          element = that.idIndex(_id);
        element.goTo();
      });
    },
    frontend: function ($dom, keyobj) {
      var gallery = $dom.data('gallery');
      var _id = this.data('_id');
      var that = this;

      if (!gallery.imageset) return;

      if (this._active && gallery._feActiveId != _id) {
        for (let i = 0, l = gallery.imageset.images.length; i < l; i++) {
          if (gallery.imageset.images[i].data._id == _id) {
            that._activeId = _id;
            gallery.show({ no: i });
            return;
          }
        }
      }
    },
    backend: function ($dom, keyobj) {},
  },

  sitePlan: {
    init: function ($dom, keyobj) {
      if ($dom.data('initiatedSitePlan')) {
        return;
      }
      $dom.data('initiatedSitePlan', true);

      // Init enum
      var $label = $dom.find('.currentPlan'),
        $optionWrapper: JQuery,
        $options = $dom.find('.option'),
        $container = $dom.closest('.changeSitePlan');

      $options.wrapAll('<div class="options"></div>');
      $optionWrapper = $options.parent().hide().css('opacity', 0);

      $dom.data('$label', $label);

      // Open / close select list
      var hidden = true;
      $dom.one('click', async function toggle() {
        if (hidden) {
          $optionWrapper
            .stop()
            .slideDown(150)
            .animate({ opacity: 1 }, { queue: false, duration: 150 });
          $container.addClass('active');
          $dom.addClass('dropped');
        } else {
          $optionWrapper
            .stop()
            .slideUp(150)
            .animate({ opacity: 0 }, { queue: false, duration: 150 });
          $container.removeClass('active');
          $dom.removeClass('dropped');
        }
        hidden = !hidden;
        if (!hidden) {
          await sleep(0);
          let checkClose = (e) => {
            if (
              $(e.target).parents().add(e.target).filter($container).length <= 0
            ) {
              toggle.call(this);
            } else if (
              $(e.target).parents().add(e.target).filter('.siteplanHeader')
                .length > 0
            ) {
              toggle.call(this);
            } else {
              $dom.closest('body').one('click', checkClose);
            }
          };
          $dom.closest('body').one('click', checkClose);
        } else {
          $dom.one('click', toggle);
        }
      });

      // Open / close confirmation or credit-card form
      var that = this;
      $options.on('click', async function recurse(e, recursive) {
        let root = that.editor.root;
        let option = this;
        let currentValue = that.data(keyobj.param);
        let newValue = $(option).data('value');
        let title = $(option).find('.title').html();
        let credits = root.property('prepaidCredit');
        let cost = $(option).data('cost');

        if (currentValue === newValue) {
          setTimeout(function () {
            $dom.closest('body').trigger('click');
          }, 100);
          return;
        }

        // Add credit card if required
        if (
          !root.validPayment() &&
          newValue > currentValue &&
          !recursive &&
          !(credits > cost)
        ) {
          var $ccConfirm = $container.find('.creditcardConfirm');
          var $cancel = $ccConfirm.find('.ccCancel');
          var $confirm = $ccConfirm.find('.ccConfirm');

          $ccConfirm.find('.planTitle').html(title);

          // Open / close the form on click
          await sleep(0);
          $ccConfirm.stop().slideDown(150);
          $dom.closest('body').one('click', async function checkClose(e) {
            let $parents = $(e.target).parents().add(e.target);
            if ($parents.filter($ccConfirm).length <= 0)
              $ccConfirm.stop().slideUp(150);
            else if ($parents.filter($cancel).length > 0)
              $ccConfirm.stop().slideUp(150);
            else if ($parents.filter($confirm).length > 0) {
              // Check if card is valid
              await that.editor.updateCard($confirm.closest('.updateCard'));
              $ccConfirm.stop().slideUp(150);
              setTimeout(function () {
                recurse.call(option, e, true);
              }, 0);
            } else {
              $dom.closest('body').one('click', checkClose);
            }
          });
          return;
        }

        // OK to charge?
        else if (!recursive) {
          var $updateConfirm = $container.find('.updateConfirm'),
            $cancel = $updateConfirm.find('.cancel'),
            $confirm = $updateConfirm.find('.confirm');

          $updateConfirm.find('.planTitle').html(title);

          // Set text for upgrade / downgrade
          if (newValue < 10) {
            $updateConfirm.find('.downgradeText').hide();
            $updateConfirm.find('.upgradeText').hide();
            $updateConfirm.find('.deactivateText').show();
          } else if (currentValue > newValue && currentValue < 900) {
            $updateConfirm.find('.downgradeText').show();
            $updateConfirm.find('.upgradeText').hide();
            $updateConfirm.find('.deactivateText').hide();
          } else {
            $updateConfirm.find('.downgradeText').hide();
            $updateConfirm.find('.upgradeText').show();
            $updateConfirm.find('.deactivateText').hide();
          }

          // Open / close the form on click
          setTimeout(function () {
            $updateConfirm.stop().slideDown(150);
            $dom.closest('body').one('click', function checkClose(e) {
              var $parents = $(e.target).parents().add(e.target);
              if ($parents.filter($updateConfirm).length <= 0)
                $updateConfirm.stop().slideUp(150);
              else if ($parents.filter($cancel).length > 0)
                $updateConfirm.stop().slideUp(150);
              else {
                if ($parents.filter($confirm).length > 0)
                  recurse.call(option, e, true);
                $dom.closest('body').one('click', checkClose);
              }
            });
          }, 0);
          return;
        }

        that.submit('onSession', 'true');
        that.data(keyobj.param, newValue);
        that.updateUI(keyobj.unique);
        setTimeout(function () {
          $dom.closest('body').trigger('click');
        }, 100);
      });
    },
    frontend: function ($dom, keyobj) {
      var $label = $dom.data('$label'),
        value = this.data(keyobj.param) || 0,
        $option: JQuery,
        $custom = $dom.find('.option.custom'),
        title;

      if (typeof value === 'undefined' || value === '' || value === null)
        value = $dom.data('defaultvalue');
      if (typeof value === 'undefined') value = -1;

      $option = $dom.find('.option[data-value=' + value + ']');
      if ($option.length <= 0) $option = $custom;
      title = $option.find('.title').html();

      $dom.find('.option.selected').removeClass('selected');
      $option.addClass('selected');

      if ($label) {
        $label.html(title);
      }

      if (!$option.is($custom)) {
        $custom.hide();
      } else {
        $custom.show();
      }
    },
    backend: function ($dom, keyobj) {},
  },

  passwordToggle: {
    init: function ($dom, keyobj) {},
    frontend: function ($dom, keyobj) {
      if (this.data('active')) {
        $dom.find('.setPassword').hide();
        $dom.find('.updatePassword').show();
      } else {
        $dom.find('.updatePassword').hide();
        $dom.find('.setPassword').show();
      }
    },
  },

  billingAddress: {
    init: function ($dom, keyobj) {
      var that = this,
        $field = $dom.find('.billingAddressField');
      setValNoFocus($field, this.property(keyobj.param));
      $dom.on('click', '.billingAddressButton', function () {
        var val = $field.val().toString();
        function hostalert(ok, message?) {
          var $parent = $field.parent(),
            $icon = $parent.find('.warningIcon'),
            $message = $parent.find('.warningMessage');
          if (ok) $icon.removeClass('sprite').removeClass('_warning');
          else $icon.addClass('sprite').addClass('_warning');
          $message.html(message);
          if (!ok) $field.css('color', '#DC2919');
          else $field.css('color', '');
        }
        if (val == '' || /.+\@.+\..+/.test(val)) {
          that.property(
            keyobj.param,
            $dom.find('.billingAddressField').val().toString()
          );
          hostalert(true);
        } else {
          hostalert(false, 'You have entered an invalid e-mail address');
        }
      });
      $dom.on('keyup', $field, function (e) {
        if (e.keyCode == 13) {
          $dom.find('.billingAddressButton').trigger('click');
        }
      });
    },
    frontend: function ($dom, keyobj) {},
  },

  payment: {
    init: function ($dom, keyobj) {
      var that = this;
      var param = keyobj.param;

      // Bind clickers (add/remove card)
      $dom.on('click', '.ccUpdate', function () {
        $dom.find('.updateCard').show();
        $dom.find('.currentCard').hide();
      });
      $dom.on('click', '.ccCancel', function () {
        var processor;
        if (that.data('paymillPayment')) {
          processor = 'paymill';
        } else {
          processor = 'stripe';
        }
        $dom.find('.updateCard').hide();
        $dom.find('.currentCard.' + processor).show();
      });
    },
    frontend: function ($dom, keyobj) {
      var now = new Date();
      var expired = false;
      var invalid = false;
      var processor = 'stripe';
      var validPayment = this.validPayment();

      if (!validPayment) {
        expired = true;
      }

      if (this.data('paymillPayment')) {
        processor = 'paymill';
      }

      // Show / hide message boxes
      if (expired) {
        $dom.find('.messageBox.expiredPayment').show();
        $dom.find('.messageBox:not(.expiredPayment)').hide();
      } else if (invalid) {
        $dom.find('.messageBox.invalidPayment').show();
        $dom.find('.messageBox:not(.invalidPayment)').hide();
      } else {
        $dom.find('.messageBox').hide();
      }

      // Determine if we should show the "cancel" button for form fill in
      if (validPayment) {
        $dom.find('.ccCancel').show();
      } else {
        $dom.find('.ccCancel').hide();
      }

      // Determine if we should show current card presentation or form
      if (keyobj.param === 'stripeId') {
        if (this.property('stripeCcLastfour')) {
          $dom.find('.updateCard').hide();
          $dom.find('.currentCard.' + processor).show();
        } else {
          $dom.find('.updateCard').show();
          $dom.find('.currentCard.' + processor).hide();
        }
      }
    },
    backend: function ($dom, keyobj) {},
  },

  paymentType: {
    init: function ($dom, keyobj, event) {
      let dom = $dom[0];
      let recurring = dom.querySelectorAll('.cardForms, .currentCard');
      for (let i = 0; i < recurring.length; i++) {
        let form = <HTMLElement>recurring[i];
        form.style.display = 'block';
      }
    },
    backend: function ($dom, keyobj, event) {},
    frontend: function ($dom, keyobj, event, smooth) {},
  },

  font: {
    init: async function ($dom, keyobj) {
      const that = this;

      if ($dom.data('fontBound')) {
        return;
      }

      let fontpicker: Fontpicker = this.editor.fontpicker;
      if (!fontpicker) {
        let { Fontpicker } = await import(
          /* webpackChunkName: "fontpicker" */ './fontpicker'
        );
        // TODO: Add site's custom fonts
        fontpicker = this.editor.fontpicker = new Fontpicker(
          this.editor.editorData.fonts,
          this.parentSite || this
        );
        this.editor.triggerAction('loadedFontPicker');
      }
      $dom.data('fontBound', true);

      // Create "visual" clone
      const $label = $('<span class="textfield textpadding clickable"></div>');
      $dom.wrap('<div class="relative"></div>');
      $dom.after($label);
      $dom.data('$label', $label);

      var cssSelector = $dom.data('fontselector');
      cssSelector = cssSelector.replace(/{%(id)}/g, function (match, capture) {
        var value = $dom.data(capture);
        if (value) {
        } else if (capture === 'id') {
          value = that.data('_id');
        }
        return value;
      });

      let cssTarget = $dom.data('fonttarget');
      let mediaQuery: string;
      if (cssTarget === 'tablet') {
        mediaQuery = cssSelectorTablet;
      } else if (cssTarget === 'mobile') {
        mediaQuery = cssSelectorMobile;
      } else if (cssTarget === 'desktop') {
        mediaQuery = cssSelectorDesktop;
      }

      // Await, let ":placeholder" plugin set placeholders first
      await sleep(1);
      let fontInfo = await fontpicker.resolve(
        $dom.val().toString() || $dom.attr('placeholder')
      );
      $label.html(fontInfo.fontName + ' ' + fontInfo.variantName);

      // Communicator
      $label.on('click', function () {
        if ($label.data('openPicker')) {
          return;
        }
        $label.data('openPicker');
        const options = {
          value: $dom.val(),
          placeholder: $dom.attr('placeholder'),
          mediaQuery: mediaQuery,
          cssSelector: cssSelector,
          $trigger: $label,
          $container: $label.closest('.editor'),
        };
        var communicator = function (message) {
          if (message.font) {
            $dom.val(message.font.string);
            $dom.change();
          }
          if (message.label) {
            $label.html(
              message.label.fontName + ' ' + message.label.variantName
            );
          }
          if (message.kill) {
            $label.data('openPicker', false);
          }
        };
        fontpicker.popup(options, communicator);
      });

      $dom.trigger('ready');
    },
    backend: function ($dom, keyobj) {
      this.property(keyobj.param, $dom.val().toString());
    },
    frontend: function ($dom, keyobj, event) {
      var value = this.value(keyobj);
      var $label = $dom.data('$label');
      var that = this;

      var cssSelector = $dom.data('fontselector');
      cssSelector = cssSelector.replace(/{%(id)}/g, function (match, capture) {
        var value = $dom.data(capture);
        if (value) {
        } else if (capture === 'id') {
          value = that.data('_id');
        }
        return value;
      });

      var cssTarget = $dom.data('fonttarget');
      if (cssTarget) {
        if (cssTarget === 'tablet') {
          cssTarget = cssSelectorTablet;
        } else if (cssTarget === 'mobile') {
          cssTarget = cssSelectorMobile;
        } else if (cssTarget === 'desktop') {
          cssTarget = cssSelectorDesktop;
        }
        cssSelector = [cssTarget, cssSelector];
      } else {
        cssSelector = [cssSelector];
      }

      if (value !== $dom.val()) {
        $dom.val(value);
      }

      requestAnimationFrame(async () => {
        let fallback = $dom.attr('placeholder');
        let font = value || fallback;
        let fontpicker: Fontpicker = this.editor.fontpicker;
        if (!fontpicker) {
          await new Promise<void>((resolve) => {
            this.editor.on('loadedFontPicker', () => {
              resolve();
            });
          });
          fontpicker = this.editor.fontpicker;
        }
        let resolvedFont = await fontpicker.resolve(font);
        try {
          fontpicker.activate(font, cssSelector);
        } catch (e) {}

        if ($label) {
          $label.html(resolvedFont.fontName + ' ' + resolvedFont.variantName);
        }
      });
    },
  },

  siteCss: {
    init: function ($dom, keyobj) {
      var param = keyobj.param,
        val = this.property(keyobj.param),
        $css = $dom.data('$css');

      if ($css && $css.length > 0) return;

      var css = document.createElement('style');
      css.type = 'text/css';
      css.media = 'screen';
      $css = $(css);
      $dom.data('$css', $css);

      // Create a hash-object for css values
      // Bind to attach() and detach() events
      var element = $dom.closest('._kkState').data('kkState');
      if (element) {
        element.on('attached', function (param, target) {
          if (target) return;
          if (!_dom.getContainingWindow($css[0]))
            $dom.closest('html').find('head').append($css);
        });
        element.on('detached', function (param, target) {
          if (target) return;
          $css.detach();
        });
        if (_dom.getContainingWindow($dom[0])) {
          $dom.closest('html').find('head').append($css);
        }
      } else {
        $dom.closest('html').find('head').append($css);
      }
    },
    frontend: function ($dom, keyobj, event, smooth) {
      var $css = $dom.data('$css'),
        param = keyobj.param,
        val = this.property(param),
        cssData = $dom.data('cssData'),
        cssText,
        css,
        resize = false;

      if (!$css || $css.length <= 0 || typeof val === 'undefined') return;

      if (!cssData) {
        cssData = {};
        $dom.data('cssData', cssData);
      }

      // Append to cssData
      if (param === 'bgColor') {
        cssData[''] = cssData[''] || {};
        cssData[''].background = val;
        cssData['.bgColor'] = cssData['.bgColor'] || {};
        cssData['.bgColor'].background = val;
      } else if (param === 'layoutBg') {
        cssData['.layoutBg'] = cssData['.layoutBg'] || {};
        cssData['.layoutBg'].background = val;
      } else if (param === 'layoutFg') {
        cssData['.layoutFg'] = cssData['.layoutFg'] || {};
        cssData['.layoutFg a'] = cssData['.layoutFg a'] || {};
        cssData['.layoutFg a'].color = val;
        cssData['.layoutFg'].color = val;
      } else if (param === 'layoutActive') {
        cssData['a.layoutFg:hover'] = cssData['a.layoutFg:hover'] || {};
        cssData['a.layoutFg.hover'] = cssData['a.layoutFg.hover'] || {};
        cssData['a.layoutFg._active'] = cssData['a.layoutFg._active'] || {};
        cssData['.layoutFg a:hover'] = cssData['.layoutFg a:hover'] || {};
        cssData['.layoutFg a.hover'] = cssData['.layoutFg a.hover'] || {};
        cssData['.layoutFg a._active'] = cssData['.layoutFg a._active'] || {};
        cssData['a.layoutFg:hover'].color = val;
        cssData['a.layoutFg.hover'].color = val;
        cssData['a.layoutFg._active'].color = val;
        cssData['.layoutFg a:hover'].color = val;
        cssData['.layoutFg a.hover'].color = val;
        cssData['.layoutFg a._active'].color = val;
      } else if (param === 'bodyFont') {
        cssData[''] = cssData[''] || {};
        cssData[''].fontFamily = val;
      } else if (param === 'headerFont') {
        cssData['.header'] = cssData['.header'] || {};
        cssData['.header'].fontFamily = val;
      } else if (param === 'site-top') {
        cssData['.fullView.fixed'] = cssData['.fullView.fixed'] || {};
        cssData['.frameBar.topBar'] = cssData['.frameBar.topBar'] || {};
        if (cssData['.fullView.fixed'].top === val + 'px') return;
        cssData['.fullView.fixed']['border-top-width'] = val + 'px';
        cssData['.frameBar.topBar'].height = val + 'px';
        resize = true;
      } else if (param === 'site-bottom') {
        cssData['.fullView.fixed'] = cssData['.fullView.fixed'] || {};
        cssData['.peekyBadge'] = cssData['.peekyBadge'] || {};
        cssData['.frameBar.bottomBar'] = cssData['.frameBar.bottomBar'] || {};
        if (cssData['.fullView.fixed']['border-bottom-width'] === val + 'px')
          return;
        cssData['.fullView.fixed'].bottom = val + 'px';
        cssData['.peekyBadge']['margin-bottom'] = val + 'px';
        cssData['.frameBar.bottomBar'].height = val + 'px';
        resize = true;
      } else if (param === 'site-left') {
        cssData['.fullView.fixed'] = cssData['.fullView.fixed'] || {};
        cssData['.frameBar.leftBar'] = cssData['.frameBar.leftBar'] || {};
        if (cssData['.fullView.fixed']['border-left-width'] === val + 'px')
          return;
        cssData['.fullView.fixed'].left = val + 'px';
        cssData['.frameBar.leftBar'].width = val + 'px';
        resize = true;
      } else if (param === 'site-right') {
        cssData['.fullView.fixed'] = cssData['.fullView.fixed'] || {};
        cssData['.peekyBadge'] = cssData['.peekyBadge'] || {};
        cssData['.frameBar.rightBar'] = cssData['.frameBar.rightBar'] || {};
        if (cssData['.fullView.fixed']['border-right-width'] === val + 'px')
          return;
        cssData['.fullView.fixed'].right = val + 'px';
        cssData['.peekyBadge']['margin-right'] = val + 'px';
        cssData['.frameBar.rightBar'].width = val + 'px';
        resize = true;
      }

      if (!_dom.getContainingWindow($css[0])) return;

      // Generate CSS
      cssText = '';
      for (let selector in cssData) {
        for (let key in cssData[selector]) {
          cssText +=
            (selector ? selector : 'body') +
            '{' +
            key +
            ':' +
            cssData[selector][key] +
            ' !important;} ';
        }
      }

      css = $css[0];
      if (css.styleSheet) css.styleSheet.cssText = cssText;
      else $css.html(cssText);

      if (smooth && resize)
        setTimeout(function () {
          try {
            let $iframe: JQuery<HTMLIFrameElement> = $('#bodyframe');
            let iframe = $iframe[0];
            let win = iframe.contentWindow;
            win.dispatchEvent(new Event('resize'));
          } catch (e) {}
        }, 250);
    },
    backend: function ($dom, keyobj) {},
  },

  newCss: {
    init: function ($dom, keyobj) {
      var selector = $dom.data('selector') || '';
      var $iframe = $('#bodyframe');
    },
    frontend: async function frontend($dom, keyobj) {
      const that = this;
      const elementId = '__css_preview';
      const elementKey = this.key;

      let $iframe: JQuery<HTMLIFrameElement> = $('#bodyframe');
      let iframe = $iframe[0];
      if (!iframe || !(<any>iframe.contentWindow).pk) {
        pk.one('iframeReady', function delayedNewCSS(param) {
          $iframe = $('#bodyframe');
          $iframe.contents().find('head').append($cssElement);
          frontend.call(that, $dom, keyobj);
        });
        return;
      }

      type Win = Window & typeof globalThis;
      const iframeWindow = <Win>iframe.contentWindow;
      const CSSMediaRule = iframeWindow.CSSMediaRule; // Needs the right scope
      const CSSStyleRule = iframeWindow.CSSStyleRule;
      let $head = $iframe.contents().find('head');
      let $cssElement = $head.find('#' + elementId);
      if ($cssElement.length <= 0) {
        var cssElement = document.createElement('style');
        cssElement.type = 'text/css';
        cssElement.media = 'screen';
        cssElement.id = elementId;
        $cssElement = $(cssElement);
        $cssElement.data('css', {});
        $head.append($cssElement);
      }
      const cssMap = $cssElement.data('css');
      const val = this.value(keyobj);
      const $siteStyle = <JQuery<HTMLStyleElement>>$head.find('#sitestyle');
      const siteStylesheet =
        $siteStyle.length > 0 ? $siteStyle[0].sheet : undefined;

      const _cssTarget = $dom.data('csstarget') || 'mobile,tablet,desktop';
      const _selector = $dom.data('selector') || '';
      const _valueSyntax = $dom.data('cssvalue') || [];
      const _fallback = $dom.data('cssfallback') || false;
      const ruleDefinitions: {
        selector: string;
        value: string;
        target?: string;
        fallback?: string;
        condition?: string;
      }[] = $dom.data('cssrules') || [{}];
      let usesFallback = false;
      let placeholder = $dom.attr('placeholder');
      const rules = {};

      // Avoid crash on wrong formatted json
      if (!Array.isArray(ruleDefinitions)) {
        //console.warn('Could not parse: ' + $dom.data('cssrules'));
        //console.warn(ruleDefinitions);
        //console.warn(JSON.parse(ruleDefinitions));
        return;
      }

      if (!siteStylesheet) {
        return; // Maybe await iframe loaded?
      }

      function equals(a: string, b: string) {
        return (
          a.trim().replace(/ \n\r/g, '') === b.trim().replace(/ \n\r/g, '')
        );
      }

      function getRule(
        insertNonExistant,
        mediaQuery: string,
        selector: string,
        property: string,
        stylesheet?: CSSMediaRule | CSSStyleSheet
      ): CSSStyleRule;
      function getRule(
        mediaQuery: string,
        selector: string,
        property: string,
        stylesheet?: CSSMediaRule | CSSStyleSheet
      ): CSSStyleRule;
      function getRule(...args: any[]): CSSStyleRule {
        let i = 0;
        let insertNonExistant =
          typeof arguments[i] === 'boolean' ? arguments[i++] : true;
        let mediaQuery: string = arguments[i++];
        let selector: string = arguments[i++];
        let property: string = arguments[i++];
        let stylesheet: CSSMediaRule | CSSStyleSheet = arguments[i++];
        let foundRuleWithoutProperty: CSSStyleRule;

        if (!stylesheet) {
          stylesheet = siteStylesheet;
        }

        let cssRules = stylesheet.cssRules;

        for (let i = 0; i < cssRules.length; i++) {
          let rule = cssRules[i];
          if (
            !mediaQuery &&
            rule instanceof CSSStyleRule &&
            equals(rule.selectorText, selector)
          ) {
            if (typeof property === 'undefined') {
              return rule;
            } else {
              for (let thisProperty in rule.style) {
                if (thisProperty === property) {
                  return rule;
                }
              }
              foundRuleWithoutProperty = rule;
            }
          } else if (
            mediaQuery &&
            rule instanceof CSSMediaRule &&
            equals('@media ' + rule.media, mediaQuery)
          ) {
            let foundRule = getRule(false, undefined, selector, property, rule);
            if (foundRule) {
              return foundRule;
            }
            if (property && !foundRuleWithoutProperty) {
              foundRuleWithoutProperty = getRule(
                false,
                undefined,
                selector,
                undefined,
                rule
              );
            }
          }
        }
        if (insertNonExistant) {
          if (foundRuleWithoutProperty) {
            return foundRuleWithoutProperty;
          }
          // Rule not found, create rule
          else if (mediaQuery) {
            //console.warn('Create media rule: ');
            //console.warn(mediaQuery + ' {' + selector + '{}}');
            let index = siteStylesheet.insertRule(
              mediaQuery + '{' + selector + '{}}',
              siteStylesheet.cssRules.length
            );
            let mediaRule = <CSSMediaRule>siteStylesheet.cssRules[index];
            return <CSSStyleRule>mediaRule.cssRules[0];
          } else {
            //console.warn('Create rule:');
            //console.warn(selector + '{}');
            let index = stylesheet.insertRule(
              selector + ' {}',
              stylesheet.cssRules.length
            );
            return <CSSStyleRule>stylesheet.cssRules[index];
          }
        }
      }

      function removeRule(mediaQuery, selector, property) {
        let rule = getRule(mediaQuery, selector, property);
        if (property && rule instanceof CSSStyleRule) {
          rule.style[property] = '';
        }
        return rule;
      }

      for (let i = 0; i < ruleDefinitions.length; i++) {
        let rule = ruleDefinitions[i];
        let cssTarget = rule.target || _cssTarget;
        let selector = rule.selector || _selector;
        let valueSyntax = rule.value || _valueSyntax;
        let fallback = rule.fallback || _fallback;
        let condition = rule.condition;
        let media: string;
        let innerVal = val;
        let enableRule = true;
        if (typeof val === 'undefined' || val === '') {
          if (!fallback) {
            enableRule = false;
          } else {
            innerVal = placeholder;
            usesFallback = true;
          }
        }

        if (condition) {
          if (!new RegExp(condition).test(val)) {
            enableRule = false;
          }
        }

        // Make an index map for lookups instead of an array
        cssTarget = cssTarget.split(',').reduce(function (map, key) {
          map[key] = true;
          return map;
        }, {});

        if (cssTarget.mobile) {
          if (cssTarget.tablet) {
            if (cssTarget.desktop) {
            } // Matches everything
            else {
              // Mobile and tablet
              media = cssSelectorMobileTablet;
            }
          } else if (cssTarget.desktop) {
            // Mobile and desktop
            media = cssSelectorMobileDesktop;
          } else {
            // Mobile
            media = cssSelectorMobile;
          }
        } else if (cssTarget.tablet) {
          if (cssTarget.desktop) {
            // Tablet and desktop
            media = cssSelectorTabletDesktop;
          } else {
            // Tablet
            media = cssSelectorTablet;
          }
        } else {
          // Desktop
          media = cssSelectorDesktop;
        }

        if (!Array.isArray(valueSyntax)) {
          valueSyntax = valueSyntax.replace(/^[\s ;]+/, '');
          valueSyntax = valueSyntax.replace(/[\s ;]+$/, '');
          valueSyntax = valueSyntax.split(/\s*,\s/);
        }

        selector = selector.replace(
          /{%([a-zA-Z]+)}/g,
          function (match, capture) {
            let value = rule[capture] || $dom.data(capture);
            if (value) {
            } else if (capture === 'id') {
              value = that.data('_id');
            }
            return value;
          }
        );

        rules[media] = rules[media] || {};
        rules[media][selector] = rules[media][selector] || {};

        for (let i = 0; i < valueSyntax.length; i++) {
          // Replace %variables with value
          let valueRules = valueSyntax[i].replace(/{%v}/g, innerVal);
          // Split syntax into rules (if there are more than one), so we can add !important to each one
          valueRules = valueRules.split(/\s*;\s/);
          for (let i = 0; i < valueRules.length; i++) {
            if (valueRules[i] !== '') {
              let split = valueRules[i].split(':');
              let property = toCamelCase(split[0]);
              if (enableRule) {
                rules[media][selector][property] = split[1];
              } else {
                rules[media][selector][property] = false;
              }
            }
          }
        }
      }
      cssMap[elementKey + keyobj.param] = rules;

      if ($dom.val() !== val && !usesFallback) {
        setValNoFocus($dom, val);
      }

      // Queue/batch stylesheet updates
      if ($cssElement.data('_af')) {
        return;
      }
      $cssElement.data('_af', true);
      await requestAnimationFramePromise(2);
      $cssElement.data('_af', false);
      let updated = false;

      function toCamelCase(rule: string) {
        return rule
          .split('-')
          .map((item, index) => {
            if (index > 0)
              return item.charAt(0).toUpperCase() + item.slice(1).toLowerCase();
            else return item.toLowerCase();
          })
          .join('');
      }

      for (let propertyName in cssMap) {
        for (let mediaQuery in cssMap[propertyName]) {
          if (mediaQuery === 'undefined') {
            mediaQuery = undefined;
          }
          for (let cssSelector in cssMap[propertyName][mediaQuery]) {
            for (let cssProperty in cssMap[propertyName][mediaQuery][
              cssSelector
            ]) {
              let cssPropertyValue =
                cssMap[propertyName][mediaQuery][cssSelector][cssProperty];
              if (cssPropertyValue === false) {
                removeRule(mediaQuery, cssSelector, cssProperty);
                updated = true;
              } else {
                let rule = getRule(mediaQuery, cssSelector, cssProperty);
                if (rule instanceof CSSStyleRule) {
                  rule.style[cssProperty] = cssPropertyValue;
                  updated = true;
                }
              }
            }
          }
        }
      }

      if (updated) {
        $cssElement.data('css', {});
        iframeWindow.dispatchEvent(new Event('resize'));
      }
    },
  },
};

class EditorElement extends PeekyEmitter {
  key: string;
  parents: EditorElement[];
  parent?: EditorElement;
  originalParent?: EditorElement;
  parentParam: string;
  editor: Editor;
  site: Editor; // Legacy
  parentSite: EditorElement;
  skipsub: string[];
  fetched = false;
  fetchedChildren = false;
  entity: string;
  entityVariant: string;
  _data: { [key: string]: any };
  _property: { [key: string]: string | number };
  file: { [key: string]: any };
  _templateParent: EditorElement;
  _kkElement = true;

  related: { [key: string]: Relation[] } = {};
  catcher$dom: { [key: string]: { $dom: JQuery; keyobj: keyObjType }[] } = {};
  pusher$dom: { [key: string]: { $dom: JQuery; keyobj: keyObjType }[] } = {};
  private _haltsubmit = 0;
  private _submitqueue: {
    element: EditorElement;
    key: string;
    param: string;
    value: any;
    reload: boolean;
  }[] = [];
  private _modified: boolean;
  private _activeElement: boolean;
  $form: any;
  $pane: JQuery<HTMLElement>;

  _original: EditorElement;
  _clones: EditorElement[];

  // These should be moved to some variable()
  _active: boolean;
  _hover: boolean;
  _textFocus: boolean;
  $cssElement: JQuery<HTMLElement>;

  nextElement: EditorElement;
  nextpathname: string;
  replaceElement: EditorElement;
  _site: EditorElement;
  _activeId: any;
  _singleDummy: { [param: string]: EditorElement };
  $formHeader: JQuery;
  $formListItem: { [key: string]: JQuery } = {};
  $paneContainer: JQuery<HTMLElement>;
  _addedRelations: boolean;
  _hasMaster: boolean;

  _demosite = false;
  _collabsite = false;
  _ownsite = false;
  _isSingleDummy = false;
  private _updateUIIndex: { [key: string]: updateUIListItem[] | boolean } = {};
  private _updateUITimer: number;

  constructor(
    key: string,
    elementobject: DatagraphElement,
    parent: EditorElement | undefined,
    site: Editor,
    parentParam: string
  ) {
    super();
    this.key = key;
    this.parents = [];
    this.parentParam = parentParam;
    this.parent = parent;
    this.originalParent = parent;
    this.editor = this.site = site;
    this.parentSite = elementobject.parentSite;
    this.fetched = elementobject.fetched !== false;
    this.fetchedChildren =
      this.fetched !== false && elementobject.fetchedChildren !== false;

    if (parent) {
      this.parents.push(parent);
    }

    // Add to index
    this.editor.elementIndexKey[key] = this;

    if (!elementobject) return;
    this.entity = elementobject.entity;
    this.entityVariant = elementobject.entityVariant;

    // If initial data is given in elementobject, add then
    this._data = {};
    this._property = {};
    this.file = {};

    // Add initial data and properties without submitting to the server
    if (elementobject.data) {
      this.data(elementobject.data, elementobject.data._id ? false : true);
    }
    if (elementobject.properties) {
      this.property(
        elementobject.properties,
        elementobject.data && elementobject.data._id ? false : true
      );
    }
  }

  template(): string {
    let template: string = this.data('template');
    if (template) {
      return template;
    }
    if (
      this._templateParent &&
      this._templateParent !== this &&
      (template = this._templateParent.template())
    ) {
      return template;
    }
    if (
      this.parent &&
      this.parent !== this &&
      (template = this.parent.template())
    ) {
      return template;
    }
    return '_default';
  }

  entitydata(key: string) {
    let editorData = this.editor.editorData;
    let template = this.template();
    let entityData = editorData.template[template]
      ? editorData.template[template][this.entity] || {}
      : {};
    var variantData = editorData.template[template]
      ? editorData.template[template][this.entity + ':' + this.entityVariant] ||
        {}
      : {};
    var defaultData = editorData.template['_default'][this.entity] || {};
    return variantData[key] || entityData[key] || defaultData[key] || '';
  }

  /**
   * A "key" format could be
   * elementKey:type.parameter:function
   * Example
   * ><elementNo1:property.link:link
   * >elementNo1:insert.title:value
   *
   * ><	// Bi-directional (default)
   * <elementNo1:pro...... (add incoming changes from element:...key)
   * >elementNo1:pro...... (propagate changes to element:...key)
   *
   */
  splitkey(key: string) {
    //var match = (/([><]{0,2})?(((id|key):)?([^:]+):)?((.+?)\.)([^:]+)(:(.*))?/).exec(key) || [];
    var indexKey = this.editor.elementIndexKey;
    var fallbacks = key.split('|');
    var objList: keyObjType[] = [];
    var defaultObj;

    for (let i = 0; i < fallbacks.length; i++) {
      var match =
        /([><]{0,2})?(((id|key):)?([^:]+):)?((.+?)\.)([^:]+)(:(.*))?/.exec(
          fallbacks[i]
        ) || [];
      var obj: keyObjType = {
        direction: match[1], // || '><',
        idorkey: match[4], // || 'key',
        idkeyvalue: match[5],
        type: match[7],
        param: match[8],
        unique: match[7] + '.' + match[8],
        func: match[10], // || 'default',
        fullkey: key,
        partkey: fallbacks[i],
      };
      if (i === 0) {
        defaultObj = obj;
      }
      obj.direction = obj.direction || defaultObj.direction || '><';
      obj.idorkey = obj.idorkey || defaultObj.idorkey || 'key';
      obj.idkeyvalue = obj.idkeyvalue || defaultObj.idkeyvalue;
      obj.func = obj.func || defaultObj.func || 'default';

      if (obj.idkeyvalue && obj.idorkey === 'key' && indexKey[obj.idkeyvalue])
        obj.element = indexKey[obj.idkeyvalue];
      else if (obj.idkeyvalue && obj.idorkey === 'id')
        obj.element = this.idIndex(obj.idkeyvalue);
      else if (objList.length > 0) obj.element = objList[0].element;
      else obj.element = this;
      objList.push(obj);
    }

    let result: keyObjType = objList[0];
    result.objList = objList;

    return result;
  }

  value(splitKey: string | keyObjType, fallbackOriginal = true) {
    if (typeof splitKey === 'string') {
      splitKey = this.splitkey(splitKey);
    }

    function getValue(element: EditorElement, type: string, param: string) {
      if (type === 'property') {
        return element.property(param);
      } else if (type === 'insert') {
        return element.data(param);
      } else if (type === 'related') {
        return (element.related[param] || []).length;
      } else {
        return '';
      }
    }
    for (let objListItem of splitKey.objList) {
      let value = getValue(
        objListItem.element,
        objListItem.type,
        objListItem.param
      );
      if (typeof value !== 'undefined' && value !== '' && value !== null) {
        return value;
      }
      if (fallbackOriginal && objListItem.element._original) {
        value = getValue(
          objListItem.element._original,
          objListItem.type,
          objListItem.param
        );
        if (typeof value !== 'undefined' && value !== '' && value !== null) {
          return value;
        }
      }
    }

    return undefined;
  }

  idIndex(id: string) {
    let element = this.editor.elementIndexId[id];
    if (!element) {
      this.editor.fetchElement(id, () => {
        element = this.editor.elementIndexId[id];
        if (element) {
          element.updateUI();
        }
      });
      return null;
    }
    let clones = element._clones || [];
    for (let i = 0; i < clones.length; i++) {
      let clone = clones[i];
      if (clone._activeElement || clone.parent._activeElement) {
        return clone;
      }
    }
    return element;
  }

  async clone(element: EditorElement) {
    // Make a new element
    let data = this.data() || {};
    let properties = this.property() || {};
    let relations = this.related || {};
    let clone =
      element ||
      new EditorElement(
        this.editor.getkey(),
        {
          entity: this.entity,
          entityVariant: this.entityVariant,
          parentSite: this.entity === 'site' ? this : this.parentSite,
        },
        this.parent,
        this.editor,
        this.parentParam
      );
    let includeData = {
      _filename: true,
      _extension: true,
    };
    let excludeData = {};
    let excludeProp = {
      sitePlan: true,
    };

    clone._original = this;
    if (!this._clones) this._clones = [];
    this._clones.push(clone);

    await this.fetch();

    // Loop data & properties & related
    for (let k in data) {
      if (includeData[k]) clone.data(k, data[k]);
      else if (k === '_id' || k === '_uuid') continue;
      else if (data[k] === null || typeof data[k] === 'undefined') continue;
      else if (excludeData[k]) continue;
      else clone.data(k, data[k]);
    }
    // Properties
    for (let k in properties) {
      if (properties[k] === null || typeof properties[k] === 'undefined')
        continue;
      if (excludeProp[k]) continue;
      clone.property(k, properties[k]);
    }

    // Relations
    for (let relationParam in relations) {
      for (let i = relations[relationParam].length - 1; i >= 0; i--) {
        let relation = relations[relationParam][i];
        let leaf = relation.element;
        // We can't make all independent, since then we won't relate to the originals
        clone.relate(
          relationParam,
          false,
          leaf,
          true,
          relations[relationParam].length - i - 1
        );
      }
    }

    // Hack for files
    if (data._filename) {
      clone.data('file._filename', data._filename);
    }

    return clone;
  }

  // Replaces "this" with a clone if parent isn't "real" parent
  async independent(
    this: EditorElement,
    parent: EditorElement,
    parameter: string
  ) {
    // We always have a parent, (cannot clone the "user" element)
    if (!parent) {
      return this;
    }

    // Collaboration-sites shouldn't be cloned
    if (this._collabsite) {
      return this;
    }

    if (this._isSingleDummy) {
      return this;
    }

    // Parent isn't real parent, a clone is needed
    let ownedByDemosite = false;
    let originalParent = this;
    while (originalParent) {
      if (originalParent._demosite && !originalParent._ownsite) {
        ownedByDemosite = true;
        break;
      }
      originalParent = originalParent.originalParent;
    }

    if (this._demosite) {
      parameter = 'site';
    }

    // If this is owned by a demosite
    if (ownedByDemosite) {
      //console.log('Clone: ' + this.key + ' ' + this.data('title') + ' - ' + this.data('_id')  + ' ' + this._collabsite + ' ' + this._demosite + ' ' + this.parent.data('_id') + ' ' + parameter);
      let clone = new EditorElement(
        this.editor.getkey(),
        {
          entity: this.entity,
          entityVariant: this.entityVariant,
          parentSite: this.entity === 'site' ? this : this.parentSite,
        },
        parent,
        this.editor,
        parameter
      );

      // Demosites shouldn't be related to parent (added to dashboard) before saving
      if (this._demosite) {
        clone.once('submit', () => {
          parent.relate(parameter, true, clone, true, this);
          parent.updateUI('related.' + parameter, true);
        });
      } else {
        clone.submit(false);
        parent.relate(parameter, true, clone, true, this);
        clone.submit(true);
      }

      // Unrelate old element after new one has been inserted
      if (!this._demosite) {
        clone.submit(false);
        clone.unrelate(this, parameter, parent);
        clone.submit(true);
      }

      // Clone() will add data and properties from this element to the clone
      clone.submit(false);
      await this.clone(clone);
      clone.submit(true);

      parent.updateUI('related.' + parameter, true);

      return clone;
    } else {
      return this;
    }
  }

  async fetch() {
    if (this.fetched) {
      return;
    }

    let _id = this._data._id;

    // Wait for _id
    if (!_id) {
      await new Promise<void>((resolve) => {
        this.on('dataChange', function checkChange(key, value) {
          if (key !== '_id') {
            return;
          }
          this.off('dataChange', checkChange);
          resolve();
        });
      });
    } else {
      await new Promise<void>((resolve) => {
        this.editor.fetchElement(_id, (err) => {
          resolve();
        });
      });
    }
  }

  //
  // @key		..
  // @element	The target (usually a $DOM object) that is bound to a given key
  //
  bind(fullkey: string, $dom: JQuery, eventName?: string) {
    if ($dom.length <= 0 || !fullkey) {
      return;
    }

    let that = this;
    let isInput = $dom.is(':input');
    let multi = fullkey.split(/\s*,\s*/);

    let bindSingle = async (index, fullkey, secondTry?) => {
      let keyobj = that.splitkey(fullkey);
      let element = keyobj.element;
      let key = keyobj.unique;

      // if element doesn't exist, we can't continue
      if (!element) {
        // Try again when site has fetched new elements
        if (!secondTry) {
          that.editor.once('fetched', function () {
            bindSingle(index, fullkey, true);
          });
        }
        return;
      }

      // Add $dom to list of bound elements for this key
      // If direction == '<', then $dom should be updated whenever key is updated
      function addCatch(element, key) {
        if (keyobj.direction !== '>') {
          if (!element.catcher$dom[key]) element.catcher$dom[key] = [];
          element.catcher$dom[key].push({
            $dom: $dom,
            keyobj: keyobj,
          });
          $dom.addClass('_pkcatch_' + keyobj.type + '_' + keyobj.param);
        }
      }
      function addPush(element, key) {
        if (keyobj.direction !== '<') {
          if (!element.pusher$dom[key]) element.pusher$dom[key] = [];
          element.pusher$dom[key].push({
            $dom: $dom,
            keyobj: keyobj,
          });
          $dom.addClass('_pkpush_' + keyobj.type + '_' + keyobj.param);
        }
      }

      // Only main element should push
      addPush(element, key);

      // Fallback elements should also catch
      const addedCatch = {};
      for (let i = 0; i < keyobj.objList.length; i++) {
        if (
          !addedCatch[
            keyobj.objList[i].element.key + ':' + keyobj.objList[i].partkey
          ]
        ) {
          addCatch(keyobj.objList[i].element, keyobj.objList[i].unique);
          addedCatch[
            keyobj.objList[i].element.key + ':' + keyobj.objList[i].partkey
          ] = true;
        }
      }

      // Run init() function
      if (
        keyobj.func &&
        CustomFunctions[keyobj.func] &&
        CustomFunctions[keyobj.func].init
      ) {
        await CustomFunctions[keyobj.func].init.call(
          keyobj.element,
          $dom,
          keyobj
        );
      }

      // Bind update on change event by default
      if (!eventName)
        eventName = $dom.data('updateon') || $dom.data('triggeron');
      if (!eventName && isInput) eventName = 'change keyup';
      if (!eventName) eventName = 'pkchange';

      // Update backend on custom events + change and keyup
      // Only bind default "change keypress" events to input fields
      // If direction == '>', then changes in the $dom should update the key
      if (eventName && keyobj.direction !== '<')
        $dom.on(eventName, function updatefun(e) {
          // This element is marked for deletion
          if ($dom.data('unbind-' + keyobj.unique))
            return $dom.off(eventName, updatefun);

          // Do updates in next tick to make sure the new value is updated
          setTimeout(function () {
            // Update backend
            element.updateBackend(fullkey, $dom, e);
            // If a "update" value is given, update another key
            // This is used to make "relate.---" also update "related.---" etc.
            var uikey = $dom.data('update') || fullkey;
            // Update UI on value change
            element.updateUI(uikey, e, true);
          }, 0);
        });

      // Set initial value
      if (keyobj.direction !== '>') {
        that.updateUI($dom, keyobj, false);
      }
    };

    // Loop each binding (multiple bindings can be given, divided by ,)
    for (let i = 0; i < multi.length; i++) {
      bindSingle(i, multi[i]);
    }

    // Clean up when element is removed
    $dom.on('destroyed', function () {
      that.unbind(fullkey, $dom);
    });
  }

  //
  // Clean up when content is removed / reloaded / replaced etc
  //
  unbind(fullkey: string, $dom: JQuery) {
    const that = this;
    const multi = fullkey.split(/\s*,\s*/);

    for (let i = 0; i < multi.length; i++) {
      let fullkey = multi[i];
      let keyobj = that.splitkey(fullkey);
      let element = keyobj.element;
      let key = keyobj.type + '.' + keyobj.param;

      if (!element) {
        continue;
      }

      let catchers = element.catcher$dom[key];
      let pushers = element.pusher$dom[key];

      if (catchers) {
        for (let j = 0; j < catchers.length; j++) {
          if (catchers[j].$dom[0] === $dom[0]) {
            catchers.splice(j--, 1);
          }
        }
      }
      if (pushers) {
        for (let j = 0; j < pushers.length; j++) {
          if (pushers[j].$dom[0] === $dom[0]) {
            pushers.splice(j--, 1);
          }
        }
      }
    }
  }

  // Update all UI elements for a given key
  async updateUI($list?, inputkey?, smooth?: boolean);
  async updateUI($list, inputkey, event: any, smooth?: boolean);
  async updateUI(...args: any[]) {
    let that = this;
    let $dom: JQuery;
    let i = 0;
    let key: string;
    let keyobj: keyObjType;
    let event = { target: true };
    let smooth = false;

    // If item isn't fetched, fetch it!
    if (that.fetched === false) {
      this.editor.fetchElement(that._data._id, (err) => {
        this.updateUI.apply(this, args);
      });
      return;
    }

    if (args[i] instanceof $) $dom = args[i++];
    if (typeof args[i] === 'object' && !args[i].target) keyobj = args[i++];
    if (typeof args[i] === 'string') key = args[i++];
    if (typeof args[i] === 'object' && args[i].target) event = args[i++];
    if (typeof args[i] === 'boolean') smooth = args[i++];

    // Get the keyobj for the *requested* key
    if (key) {
      keyobj = this.splitkey(key);
    }

    if (keyobj && keyobj.element !== this) {
      return keyobj.element.updateUI(keyobj, event, smooth);
    }

    // Don't add duplicates
    let index = this._updateUIIndex;
    let add = (keyobj: keyObjType, $dom?: JQuery, smooth?: boolean) => {
      if (!keyobj) {
        for (let key in this.catcher$dom) {
          for (let i = 0; i < this.catcher$dom[key].length; i++) {
            add(
              this.catcher$dom[key][i].keyobj,
              this.catcher$dom[key][i].$dom,
              smooth
            );
          }
        }
        return;
      }
      if (index['*']) {
        return;
      }
      let indexItem = index[keyobj.unique];
      if (indexItem === true) {
        return;
      }
      if (indexItem === false) {
        return;
      }
      if (!$dom) {
        index[keyobj.unique] = true;
        return;
      }
      if (Array.isArray(indexItem)) {
        let found = false;
        for (let i = 0; i < indexItem.length; i++) {
          let indexItemItem = indexItem[i];
          let $indexedDom = indexItemItem.$dom;
          if ($dom[0] === $indexedDom[0]) {
            found = true;
            indexItemItem.smooth = indexItemItem.smooth || smooth;
            break;
          }
        }
        if (!found) {
          indexItem.push({
            keyobj: keyobj,
            $dom: $dom,
            smooth: smooth,
          });
        }
      } else {
        indexItem = index[keyobj.unique] = [];
        indexItem.push({
          keyobj: keyobj,
          $dom: $dom,
          smooth: smooth,
        });
      }
    };

    // $DOM elements are specified, find required keys (and elements) if needed
    if ($dom) {
      $dom.each(function () {
        let $this = $(this);
        let keys: string[] = (
          keyobj ? keyobj.fullkey : $this.data('bind')
        ).split(/\s*,\s*/);
        let foreign = {};

        for (let value of keys) {
          let obj = that.splitkey(value);
          if (keyobj && keyobj.unique !== obj.unique) continue;
          if (obj.element === that) {
            add(keyobj, $dom, smooth);
          } else {
            if (foreign[obj.element.key + ':' + obj.unique]) continue;
            foreign[obj.element.key + ':' + obj.unique] = true;
            obj.element.updateUI($this, obj, event, smooth);
          }
        }
      });
    }

    // Update all keys
    else {
      // keyobj and $dom can be undefined. Handlend in add()
      add(keyobj, undefined, smooth);
    }

    // Create timer when needed
    if (!this._updateUITimer) {
      this._updateUITimer = requestAnimationFrame(() => {
        let list: updateUIListItem[] = [];
        let index = this._updateUIIndex;
        this._updateUIIndex = {};
        this._updateUITimer = 0;

        if (index['*']) {
          // Everything
          for (let key in this.catcher$dom) {
            let catcher$domItem = this.catcher$dom[key];
            for (let i = 0; i < catcher$domItem.length; i++) {
              list.push({
                keyobj: catcher$domItem[i].keyobj,
                $dom: catcher$domItem[i].$dom,
                //smooth: catcher$domItem[i].smooth,
              });
            }
          }
        }

        for (let key in index) {
          let indexItem = index[key];
          if (indexItem === true) {
            // Add from catch$dom
            let catcher$domItem = this.catcher$dom[key] || [];
            for (let i = 0; i < catcher$domItem.length; i++) {
              list.push({
                keyobj: catcher$domItem[i].keyobj,
                $dom: catcher$domItem[i].$dom,
                //smooth: catcher$domItem[i].smooth,
              });
            }
          } else if (indexItem === false) {
          } else {
            for (let i = 0; i < indexItem.length; i++) {
              list.push(indexItem[i]);
            }
          }
        }

        executeUpdate(list);
      });
    }

    let executeUpdate = async (list: updateUIListItem[]) => {
      for (let listItem of list) {
        let $target: JQuery = listItem.$dom;
        let keyobj = listItem.keyobj;
        let tagname = $target[0].tagName;
        let type = keyobj.type;
        let param = keyobj.param;
        let func = keyobj.func;
        let domSmooth = $target.data('smooth');
        let innerSmooth = smooth || listItem.smooth;

        // Allow setting "smooth" for each dom
        if (domSmooth || domSmooth === false) {
          innerSmooth = domSmooth !== false && domSmooth !== 'false';
        }

        // Execute "func" if it exists
        if (func !== 'default') {
          if (CustomFunctions[func] && CustomFunctions[func].frontend) {
            CustomFunctions[func].frontend.call(
              keyobj.element,
              $target,
              keyobj,
              event,
              innerSmooth
            );
            continue;
          }
        }

        // Update string values
        if (type === 'insert' || type === 'property') {
          var value = that.value(keyobj);

          // Checkbox
          if ($target.is(':checkbox')) {
            if ($target.prop('checked') !== (value && value !== 'false')) {
              $target.prop('checked', value && value !== 'false');
              $target.trigger('change');
            }
          }
          // Update string
          else if (/(input|textarea)/i.test(tagname)) {
            if (typeof value !== 'undefined' && value !== '') {
              if ($target.val().toString() != value)
                setValNoFocus($target, value);
            } else {
              setValNoFocus($target, $target.data('placeholder') || '');
            }
          } else if (/img/i.test(tagname)) {
            $target.hide();
            if (!$target.data('path') && value) {
              $target.attr('src', value);
            } else if (value) {
              $target.attr(
                'src',
                ($target.data('path') || '/_image/') +
                  value +
                  '.' +
                  ($target.data('extension') || 'png')
              );
            }
            // Not yet loaded
            if (
              !_dom.imgloaded($target[0]) &&
              ($target.attr('src') || !$target.data('nosrc'))
            ) {
              //$target.css('opacity', 0.1);
              if (!$target.data('$spinner')) {
                (function () {
                  var $spinner = $(
                    '<div class="pk-spinner pk-spin pk-small"></div>'
                  ).hide();
                  $target.data('$spinner', $spinner);
                  $target.parent().append($spinner);
                  $spinner.fadeIn();
                  $target.one('load error', function checkLoad(e) {
                    if (e.type !== 'error') {
                      $target.fadeTo(150, 1);
                      $spinner.fadeTo(150, 0, function () {
                        $spinner.remove();
                      });
                    }
                    $target.one('load error', checkLoad);
                  });
                })();
              }
            }
            // Nothing to load
            else if (!$target.attr('src')) {
              $target.hide();
            }
            // Already loaded
            else {
              if (innerSmooth) {
                $target.fadeTo(150, 1);
              } else {
                $target.show();
              }
            }
          } else {
            $target.html(value || $target.data('placeholder') || '');
          }
        }

        // Update relations
        else if (type === 'related') {
          // Loop DOM to check if everything is in order
          let $existing = $target.children('._pkelement:not(._clone)');
          let $after: JQuery;
          let related = (that.related[param] = that.related[param] || []);
          let $index: { [key: string]: JQuery } = {};
          let $template: JQuery = $target.data('$template');
          let initiated: boolean = $target.data('initiated');
          let templateName: string = $target.data('template') || '_formList';
          // Define how template children will be positioned.
          // "template": the children will be used as a template, instead of rendering a new one through render()
          // "prepend": the children will be prepended before all relations
          // "append": the children will be appended after all relations
          let childrenType = $target.data('childrentype') || 'template'; // Could be template, append, prepend
          $target.data('initiated', true);
          if (
            !initiated &&
            !$template &&
            $target.children().length > 0 &&
            childrenType === 'template'
          ) {
            //$template = $('<div></div>').append($target.children());
            $template = $target.children();
            $template.detach();
            $target.data('$template', $template);
            $target.addClass('_pktemplate');
          } else {
            this.bindEvents($target);
          }

          if ($target.data('single')) {
            if (!that._singleDummy) {
              that._singleDummy = {};
            }
            // Dummy has been saved (and possibly removed)
            if (
              that._singleDummy[param] &&
              that._singleDummy[param].data('_id')
            ) {
              that._singleDummy[param] = undefined;
            }
            if (
              !that._singleDummy[param] &&
              related.length <= 0 &&
              $target.data('entity')
            ) {
              let dummy = new EditorElement(
                that.editor.getkey(),
                {
                  entity: $target.data('entity'),
                  entityVariant: $target.data('entityvariant'),
                  parentSite: that.parentSite,
                },
                that,
                that.editor,
                param
              );
              dummy.submit(false);
              that._singleDummy[param] = that.relate(param, dummy, false);
              dummy.submit(true);
              that._singleDummy[param]._isSingleDummy = true;
            } else if (related.length > 1 && that._singleDummy[param]) {
              that._singleDummy[param].submit(false);
              that._singleDummy[param].unrelate(param, that);
              that._singleDummy[param] = undefined;
            }
          }

          // Create an index of existing elements
          for (let i = 0; i < $existing.length; i++) {
            let element: EditorElement = $($existing[i]).data('element');
            if (element) {
              $index[element.key] = $($existing[i]);
            }
          }

          // Append or move every element
          // And re render templates to get correct context
          for (let i = related.length - 1, j = 0; i >= 0; i-- && j++) {
            let relation = related[i];
            let leaf = relation.element;
            leaf = relation.element = await relation.element.independent(
              that,
              relation.param
            );

            let leafKey: string = leaf.key;
            let $current = $index[leafKey];

            // Testing, should work:
            leaf.entityVariant = $target.data('entityvariant');
            if (
              leaf.$formListItem[templateName] &&
              $target.is(leaf.$formListItem[templateName].data('$target'))
            ) {
              // Nothing to do
            } else {
              // Set context-based parent and parameter
              (<any>leaf)._parent = that;
              (<any>leaf)._parameter = param;
              (<any>leaf)._templateParent =
                $target.closest('._pkelement').data('element') || that;
              (<any>leaf)._templateOptions =
                $target.data('templateoptions') || {};

              if (leaf.$formListItem[templateName]) {
                leaf.unbindEvents(leaf.$formListItem[templateName]);
              }

              // Demosites will not have 'parent' set, for elements that aren't really parents
              if (!leaf.parent && !leaf.isRoot()) {
                leaf.parent = that;
              }

              $current = leaf.$formListItem[templateName] = leaf.render(
                $template || templateName
              );
              if (!$current) {
                console.log(
                  'Not rendered: ' +
                    $target.data('template') +
                    ' ' +
                    $target.data('entityvariant') +
                    ' Entity: ' +
                    $target.data('entity') +
                    ' ' +
                    leaf.data('template')
                );
                //console.log(leaf);
              } else {
                $current.data('$target', $target);
              }

              delete (<any>leaf)._parent;
              delete (<any>leaf)._parameter;
              delete (<any>leaf)._templateParent;
              delete (<any>leaf)._templateOptions;
            }

            if (!$current) {
              //console.log('Unable to render ' + $target.data('template'));
              //console.log($target.data('entity') + ' ' + leaf.entity);
              //console.log(_pk.template[leaf.template()]);
              continue;
            }

            // Item exists in UI
            if ($index[leafKey]) {
              if (
                !$current.is($index[leafKey]) &&
                !_dom.getContainingWindow($current[0])
              ) {
                $index[leafKey].replaceWith($current);
                $current.find('input,textarea').trigger('created');
              } else {
              }
              delete $index[leafKey];
            }
            // Item doesn't exist in UI
            else {
              if (j > 0) {
                $after = $target.children('._pkelement').eq(-j);
              } else {
                $after = undefined;
              }

              if (innerSmooth) {
                $current.hide();
              }
              // The following element is found, insert new one before
              if ($after && $after.length > 0) {
                $after.before($current);
              }
              // If there are other elements, append to list
              else if ($existing.length > 0) {
                $existing.last().after($current);
              }
              // If there are no elements, and existing (optional) content should be appended,
              // insert new element before
              else if (childrenType === 'append') {
                $target.prepend($current);
              }
              // If there are no elements, and existing (optional) content should be prepended,
              // insert new element after
              else {
                $target.append($current);
              }
              if (innerSmooth) {
                $current.slideDown(150);
              }

              // Fire "created" event for elements
              $current.find('input,textarea').trigger('created');
            }
          }

          // Remove remaining elements
          for (let k in $index) {
            if (innerSmooth) {
              $($index[k]).slideUp(150, function () {
                $(this).remove();
              });
            } else {
              $($index[k]).remove();
            }
          }
        }

        //
        else if (type === 'file') {
        }
      } // for of list
    }; // executeUpdate()
  }

  /*
   * These functions should:
   * - Set the new data in the "backend object"
   * - Set the required changes in the editors "changes" array, ready for uploading
   * - Update all bound DOM objects to the new value
   */
  updateBackend(fullkey: string, $dom: JQuery, event?) {
    let keyobj = this.splitkey(fullkey);
    let func;

    // Make sure this is executed on the correct element
    if (keyobj.element && keyobj.element !== this) {
      return keyobj.element.updateBackend(fullkey, $dom);
    }

    // Find the "backend" function to execute
    if (CustomFunctions[keyobj.func]) {
      func = CustomFunctions[keyobj.func].backend;
    }
    if (!func) func = CustomFunctions['default'].backend;

    // Execute function
    func.call(keyobj.element, $dom, keyobj, event);
  }

  //
  // A child element with a parameter
  // Key: relate:parameter
  //
  relate(
    parameter: string,
    master: boolean,
    element: EditorElement | number | string | DatagraphElement,
    submit?: boolean,
    $dom?: JQuery,
    position?: number | EditorElement
  );
  relate(
    parameter: string,
    master: boolean,
    element: EditorElement | number | string | DatagraphElement,
    submit?: boolean,
    position?: number | EditorElement
  );
  relate(
    parameter: string,
    element: EditorElement | number | string | DatagraphElement,
    submit?: boolean,
    $dom?: JQuery,
    position?: number | EditorElement
  );
  relate(
    parameter: string,
    element: EditorElement | number | string | DatagraphElement,
    submit?: boolean,
    position?: number | EditorElement
  );
  relate(...args: any[]) {
    let i = 0;
    let parameter = args[i++];
    let master = typeof args[i] === 'boolean' ? args[i++] : undefined;

    let _id: string;
    let element: EditorElement;
    let elementobject: DatagraphElement;
    let index: number;
    let parentSite: EditorElement;
    let move: EditorElement;

    if (typeof args[i] === 'number') {
      _id = args[i++];
      element = this.idIndex(_id);
    } else if (typeof args[i] === 'object') {
      if (args[i]._kkElement) {
        element = args[i++];
        _id = element.data('_id');
      } else {
        elementobject = args[i++];
      }
    }
    let submit: boolean = typeof args[i] === 'boolean' ? args[i++] : undefined;
    let $dom: JQuery = args[i] instanceof $ ? args[i++] : undefined;
    let position: number | EditorElement = args[i++];

    // If parameter-array doesn't exist, create it
    let related = (this.related[parameter] = this.related[parameter] || []);

    // Check if element already exists
    if (
      !element &&
      elementobject &&
      elementobject.data &&
      elementobject.data._id &&
      this.idIndex(elementobject.data._id)
    ) {
      element = this.idIndex(elementobject.data._id);
    }

    // Lookup index if 'position' is given as element
    if (position instanceof EditorElement) {
      for (let i = 0; i < related.length; i++) {
        if (related[i].element === position) {
          index = i;
          break;
        }
      }
    }
    // Create a new element
    if (!element) {
      // Something wrong has happened!
      if (!elementobject) {
        return;
      }
      if (this.entity === 'site') {
        parentSite = this;
      } else {
        parentSite = this.parentSite;
      }
      element = new EditorElement(
        this.editor.getkey(),
        $.extend({ parentSite: parentSite }, elementobject),
        this,
        this.editor,
        parameter
      );
      index = elementobject.index;
    } else {
      // Look for element in relation list
      for (let i = 0; i < related.length; i++) {
        if (related[i].element === element) {
          return element;
        }
        if (related[i].element._original === element) {
          return element;
        }
      }
    }

    if ($dom && $dom.data('append')) {
      index = -1;
    }

    let relation = {
      element: element,
      param: parameter,
      master: master ? 1 : 0,
    };
    // Append this element
    if (index >= related.length || index < 0) {
      if (related.length > 0) {
        move = related[related.length - 1].element;
      }
      related.push(relation);
    }
    // Add element somewhere in the middle
    else if (index > 0 && index < related.length) {
      move = related[index].element;
      related.splice(index, 0, relation);
    }
    // Prepend this element
    else {
      related.unshift(relation);
    }

    // If no data is given, this item is added manually and the relation should be created
    // (Relations are also initiated on launch, to get the UI set up correctly. Those should not be submitted)
    if (
      (_id && submit !== false) ||
      submit ||
      (!_id && !elementobject) ||
      (!_id && !elementobject.data)
    ) {
      if (elementobject && elementobject.file) {
        element.savefile('file', elementobject.file);
      }

      if (this._haltsubmit) {
        element.submit(false);
      }

      let relationKey = element.key;
      if (typeof position === 'number') {
        relationKey = position + '-' + relationKey;
      }

      element.submit(this, '_id', this);
      element.submit(
        this,
        'entity',
        this.entity,
        $dom && $dom.data('noreload') ? false : true
      );
      element.submit(this, 'relate.' + relationKey + '.element', element.key);
      element.submit(this, 'relate.' + relationKey + '.param', parameter);
      // Hack, add triggersub if parent doesn't exist
      if (this._haltsubmit) {
        this.submit(element, '_triggersub.' + element.key, element);
      } else {
      }
      if ($dom && $dom.data('twoway')) {
        element.submit(this, 'twoway', 'true');
      }
      if (typeof position === 'number') {
        element.submit(
          this,
          'relate.' + relationKey + '.order',
          position.toString()
        );
      }
      if (move) {
        element.submit('move.target.key', move.key);
        element.submit('move.relation.root.key', this.key);
        element.submit('move.relation.param', parameter);
        element.submit(move, 'entity', move.entity);
        element.submit(move, '_id', move);
      }
      // If "element" doesn't have an id, it is created now and this should be a master relation
      if (!element.data('_id') || master) {
        // This makes it submit "master" only when creating a new element, not relating to an old one
        // e.g. if element._original is used
        if (!element._hasMaster && master !== false) {
          element.submit(this, 'relate.' + relationKey + '.master', '1');
          element._hasMaster = true;
          element.submit('_triggersub.' + this.key, this);
          element.submit('save.trigger', 'true');
          relation.master = 1;
        }
      }
      element.submit('_id', element);
      element.submit(
        'entity',
        element.entity,
        $dom && $dom.data('noreload') ? false : true,
        true
      );
      if (this._haltsubmit) {
        element.submit(true);
      }
    }

    this.trigger('relate', parameter, relation);
    return element;
  }

  //
  // This will unrelate an element from its parent. All relations will be "master" relations
  // so the unrelated element will be deleted unless there are other owners as well
  //
  unrelate(
    element: EditorElement,
    parameter: string,
    parent: EditorElement,
    $dom?: JQuery
  );
  unrelate(parameter: string, parent: EditorElement, $dom?: JQuery);
  unrelate(...args: any[]) {
    let i = 0;
    let element: EditorElement =
      args[i] instanceof EditorElement ? args[i++] : this;
    let parameter: string = args[i++];
    let parent: EditorElement = args[i++];
    let $dom: JQuery = args[i++];

    let parameters: string[];
    let match: Relation[];

    // Remove from specified parent
    if (parameter) parameters = [parameter];
    else parameters = Object.keys(parent.related || {});

    // Remove self from this parent
    for (let i = 0; i < parameters.length; i++) {
      match = parent.related[parameters[i]];
      for (let j = 0; j < match.length; j++) {
        let relation = match[j];
        if (relation.element === element) {
          match.splice(j--, 1);
        }
      }
    }

    // Add to submit queue
    this.submit(parent, 'entity', parent.entity);
    this.submit(parent, '_id', parent);
    //this.submit(parent, 'unrelate.' + element.key, element.key + ':' + (element.data('_relation_param') || parameter)); // A reload is needed after submitting (deleting)
    this.submit(parent, 'unrelate.' + element.key + '._id', element);
    this.submit(
      parent,
      'unrelate.' + element.key + '.param',
      element.data('_relation_param') || parameter
    );
    if ($dom && $dom.data('twoway')) {
      this.submit(parent, 'twoway', 'true');
    }
    this.submit(
      element,
      '_id',
      element,
      $dom && $dom.data('noreload') ? false : true,
      true
    ); // Force reload after submit + submit immediately
  }

  //
  // A "value" can be a string
  // Key: value:fieldname
  //
  data();
  data(key: string);
  data(
    key: string,
    value: string | number,
    submit?: boolean,
    reload?: boolean
  ): void;
  data(
    object: { [key: string]: string | number },
    submit?: boolean,
    reload?: boolean
  ): void;
  data(key?, value?, submit?, reload?) {
    // Return all data if nothing is given
    if (typeof key === 'undefined') {
      return this._data;
    }

    // Return data if no value is given
    if (typeof key === 'string' && typeof value === 'undefined') {
      return this._data[key];
    }

    // Data can be given as an object
    if (typeof key === 'object') {
      for (let k in key) this.data(k, key[k], value);
      return;
    }

    if (value === null) {
      return;
    }

    // Replace \r\n with \n
    if (typeof value === 'string') {
      value = value.replace(/\r\n/g, '\n');
    }

    // If key is the same as the one currently set, don't do anything
    if (this._data[key] === value) {
      return;
    }

    // If the specified value is an ID, then update the indexes
    if (key === '_id') {
      // Remove previous entry (if present)
      if (this._data._id) {
        this.editor.elementIndexId[this._data._id] = undefined;
      }
      // Add new entry
      this.editor.elementIndexId[value] = this;
      // A new element could be added, so we need a reload after submitting
      reload = true;
    }

    // If the key is a "catcharg" then we need a reload after the submit
    if (key === this.entitydata('catcharg')) {
      reload = true;
    }

    // CRLF -> LF
    if (typeof value === 'string') {
      value = value.replace(/\r\n?/gm, '\n');
    }

    // Set the key
    this._data[key] = value;

    // If we should submit to the server, queue it
    if (submit !== false) {
      this.submit('insert.' + key, value.toString());
      this.submit('entity', this.entity);
      this.submit('_id', this, reload);
      this.trigger('changed');
    }

    this.trigger('dataChange', key, value);
  }

  objectToQueries(object, prefix) {
    var result = {};
    (function recurse(object, string) {
      for (let k in object) {
        if (typeof object[k] === 'object') recurse(object[k], string + '.' + k);
        else if (typeof object[k] === 'undefined') continue;
        else result[string + '.' + k] = object[k];
      }
    })(object, prefix || '');

    return result;
  }

  //
  //
  // Key: property:propertyname
  //
  property();
  property(key: string);
  property(
    key: string,
    value: string | number,
    submit?: boolean,
    reload?: boolean
  ): void;
  property(
    object: { [key: string]: string | number },
    submit?: boolean,
    reload?: boolean
  ): void;
  property(key?, value?, submit?, reload?) {
    if (typeof key === 'undefined') {
      return this._property;
    }

    // Return data if no value is given
    if (typeof key === 'string' && typeof value === 'undefined') {
      return this._property[key];
    }

    // If this is given as an object, transform it into query string format
    if (typeof value === 'object') {
      key = this.objectToQueries(value, key);
      value = submit;
      submit = reload;
      reload = undefined;
    }

    // Data can be given as an object
    if (typeof key === 'object') {
      for (let k in key) {
        this.property(k, key[k], value, submit); // value, submit and reload are skewed one position
      }
      return;
    }

    if (value === null) return false;
    if (this._property[key] === value.toString()) return false;
    this._property[key] = value.toString();

    // Add change to submit queue
    if (submit !== false) {
      this.submit('property.' + key, value.toString());
      this.submit('entity', this.entity);
      this.submit('_id', this, reload);
      this.trigger('changed');
    }

    this.trigger('propertyChange', key, value);
    this.editor.trigger('elementPropertyChange', this.key, key, value);
  }

  //
  // Key: value:fieldname
  //
  savefile(key = 'file', file: File) {
    let that = this;

    // Detect videos, upload them to vimeo
    if (
      this.entity === 'image' &&
      file.name &&
      file.name.match(
        /\.(mp4|m4v|f4v|mov|qt|avi|mkv|wmv|asf|asx|divx|dv|dvx|mpeg|mpg|3g2|3gp|3ivx|3vx)$/i
      )
    ) {
      this.property('_vimeoGetTicket', 'true');
      this.property('_vimeoFileSize', file.size?.toString() || 0);
      this.property('engine', 'video');

      this.one(
        'propertyChange',
        async function startUpload(this: EditorElement, key, value) {
          if (key !== '_vimeoUploadLink') {
            this.one('propertyChange', startUpload);
            return;
          }
          const fileSize = file.size;
          const progress = this.editor.progressInstance(0, fileSize);
          const status = this.editor.status('Uploading video&hellip;');
          const tus = await import(
            /* webpackChunkName: "tus-js-client" */ 'tus-js-client'
          );
          console.log('UPLOAD.');
          const upload = new tus.Upload(file, {
            uploadUrl: this.property(key),
            metadata: {
              filename: file.name,
              filetype: file.type,
            },
            onError: (error) => {
              status('An error occured when uploading video', 120);
              console.log('Error');
              progress(fileSize, fileSize);
            },
            onProgress: (uploaded, total) => {
              console.log('Progress: ' + uploaded + ' ' + total);
              progress(uploaded, total);
            },
            onSuccess: () => {
              console.log('SUCCESS');
              this.property('_vimeoComplete', Math.random() * 1000);
              progress(fileSize, fileSize);
              // Loop until video has been transcoded
              status('Preparing video&hellip;');
              setTimeout(async function checkUpdated() {
                let img = new Image();
                img.src =
                  '/_image/' +
                  that.data('_filename') +
                  '.jpg?' +
                  new Date().getTime();

                try {
                  await _dom.imgload(img);
                } catch (e) {
                  // File not found, try again in 20 seconds
                  setTimeout(checkUpdated, 20000);
                  return;
                }
                // OK done
                that.property('_vimeoCheckStatus', Math.random() * 100);
                that.updateUI();
                window.pk.trigger('reloadPreview');
                status();
              }, 20000);

              // Check for errors
              this.one('propertyChange', function checkError(key, value) {
                if (key === '_vimeoError') {
                  that.unrelate(that.parentParam, that.parent);
                  that.parent.updateUI();
                  pkalert('We could not process your video file.');
                } else if (key !== '_vimeoLocation') {
                  that.one('propertyChange', checkError);
                }
              });
            },
          });
          upload.start();
        }
      );
    } else {
      this.data('_filename', '');
      this.submit('insert.file', file, true, true);
      this.updateUI();
    }
    this.submit('entity', this.entity);
    this.submit('_id', this);
  }

  //
  // Bind all data-bind links found in a template
  //
  async bindEvents($template: JQuery) {
    const that = this;
    const scopeSelector = '[data-childrentype]';
    const bindSelector = '[data-bind]';
    if ($template.is('._boundEvents')) {
      return;
    }
    $template.addClass('_boundEvents');

    // Don't call on nested elements at first, do this recursively
    const $allElements = $template
      .find(bindSelector)
      .add($template.filter(bindSelector));
    const $excludeElements = $template.find(scopeSelector + ' ' + bindSelector);
    const $filteredElements = $allElements
      .not($excludeElements)
      .not('._pkbound');

    for (let i = 0; i < $filteredElements.length; i++) {
      const element = $filteredElements[i];
      const $element = $(element);
      const key = $element.data('bind');
      if (!key) {
        return;
      }
      $element.addClass('_pkbound');

      if ($element.data('bindonfocus')) {
        let focusObserver = new FocusObserver(element);
        focusObserver.activate();
        focusObserver.oneFocus(() => {
          that.bind(key, $element);
          if (!$element.is($template) && !$element.is(scopeSelector)) {
            that.bindEvents($element);
          }
        });
      } else {
        that.bind(key, $element);
        if (!$element.is($template) && !$element.is(scopeSelector)) {
          that.bindEvents($element);
        } else {
          //console.log('DONT BIND EVENTS FURTHER ' + $element.attr('class'));
          //console.log($element);
          //console.log($element.is($template) + ' ' + $element.is(scopeSelector));
        }
      }
    }

    return $template;
  }

  unbindEvents($template: JQuery) {
    let that = this;
    const scopeSelector = '[data-childrentype]';
    const bindSelector = '[data-bind]';

    // Don't call on nested elements at first, do this recursively
    const $allElements = $template
      .find(bindSelector)
      .add($template.filter(bindSelector));
    const $excludeElements = $template.find(scopeSelector + ' ' + bindSelector);
    const $boundElements = $allElements
      .not($excludeElements)
      .filter('._pkbound');

    $boundElements.each(function () {
      var $this = $(this);
      var key = $this.data('bind');
      if (!key) return;
      that.unbind(key, $this);
      $this.removeClass('_pkbound');
    });

    return $template;
  }

  //
  // Render a given template
  //
  render(template: string | JQuery) {
    // Find parent site that contains this element
    let parent = this.parent;
    while (parent && !this._site) {
      if (parent.entity === 'site') {
        this._site = parent;
      }
      parent = parent.parent;
    }

    // Render template if it's not given as JQuery
    let $template: JQuery;
    if (typeof template === 'string') {
      if (!this.entitydata('render')[template]) {
        return;
      }

      // Find a way to get template name
      $template = $(this.entitydata('render')[template](this));
    } else {
      $template = template.clone();
    }
    $template.addClass('_pkelement');
    $template.data('element', this);

    this.bindEvents($template);
    this.editor.trigger('renderedFormFragment', $template, this);
    this._site = undefined;

    return $template;
  }

  // Set the correct URL (for the preview pane)
  // Specify a delay so we can finish the panel-animation before this anmation (avoid skipping)
  async activateUrl(delay?, options?, param?);
  async activateUrl(...args: any[]) {
    let i = 0;
    let delay: number = typeof args[i] === 'number' ? args[i++] : 0;
    let options = typeof args[i] === 'object' ? args[i++] : {};
    let param = typeof args[i] === 'object' ? args[i++] : {};
    let pathparent: EditorElement = this;
    let path = '';
    let nogo = param.ipkRecurse ? true : false;

    options._editorTriggered = true;

    let currentPath = (this.editor._currentPath || '').split('/');
    currentPath.splice(0, 2); // Remove /admin
    let newPathArray = [];

    // Build path, from last to first
    while (pathparent) {
      await sleep(1);
      if (
        pathparent.entitydata('catcharg') &&
        pathparent.data(pathparent.entitydata('catcharg'))
      ) {
        newPathArray.unshift(
          pathparent.data(pathparent.entitydata('catcharg'))
        );
        let pathPrefix = pathparent.entitydata('pathprefix');
        if (pathPrefix) {
          if (!Array.isArray(pathPrefix)) {
            pathPrefix = [pathPrefix];
          } else {
            pathPrefix = pathPrefix.slice();
          }
          for (let i = 0; i < pathPrefix.length; i++) {
            if (typeof pathPrefix[i] === 'object') {
              pathPrefix[i] =
                pathparent.parent.value(pathPrefix[i].key) ||
                pathPrefix[i].fallback;
            }
          }
          newPathArray = pathPrefix.concat(newPathArray);
        }
      }
      pathparent = pathparent.parent;
    }

    for (let i = 0; i < newPathArray.length; i++) {
      let splitPath = newPathArray[i].toString().split(',');
      if (splitPath.indexOf(currentPath[i]) >= 0) {
        newPathArray[i] = currentPath[i];
      } else {
        newPathArray[i] = splitPath[0];
      }
    }

    // Could we add more? Look through entities that has additional pathPrefix
    let remaining = [];
    for (let i = 0; i < currentPath.length; i++) {
      if (i < newPathArray.length && currentPath[i] !== newPathArray[i]) {
        break;
      }
      if (i > newPathArray.length - 1) {
        remaining.push(currentPath[i]);
      }
    }

    let prefixDepth = 0;
    if (remaining.length > 0) {
      for (let parameter in this.related) {
        let relations = this.related[parameter];
        for (let i = 0; i < relations.length; i++) {
          let relation = relations[i];
          let searchelement = relation.element;
          let pathprefix = searchelement.entitydata('pathprefix');
          if (Array.isArray(pathprefix)) {
            for (let j = prefixDepth; j < pathprefix.length; j++) {
              if (pathprefix[j] === remaining[j]) {
                newPathArray.push(pathprefix[j]);
                prefixDepth = j + 1;
              } else {
                break;
              }
            }
          }
        }
      }
    }

    path = '/admin/';
    if (newPathArray.length > 0) {
      path += newPathArray.join('/') + '/';
    }
    //options.reload = 'changed'; // Why is this needed? It caused double-fetch when following links in preview window.

    // If this is a site that is waiting for a key (a newly added site), then load a site called '_'
    // This will load an empty preview window with a spinner
    /*
    if (path === '/admin/' && this.entity === 'site' && !this.data('key')) {
      path = '_';
    }
    */

    if (
      (this.editor._currentPath && path !== this.editor._currentPath) ||
      (options && options.reload)
    ) {
      if (!nogo) {
        pk.goTo(path, options);
      }
    }

    this.editor._currentPath = path;
  }

  submit(
    element: EditorElement,
    param: string,
    value: string | EditorElement | File,
    reload?: boolean,
    immediate?: boolean
  );
  submit(
    param: string,
    value: string | EditorElement | File,
    reload?: boolean,
    immediate?: boolean
  );
  submit(activate: boolean);
  submit();
  submit(...args: any[]) {
    let i = 0;
    let element: EditorElement = typeof args[i] === 'object' ? args[i++] : this;
    let param: string | undefined =
      typeof args[i] === 'string' ? args[i++] : undefined;
    let value = args[i++];
    let reload = args[i++];
    let immediate = args[i++];

    // Halt / start submission
    if (args[0] === false) {
      this._haltsubmit++;
      return this;
    }
    if (args[0] === true) {
      this._haltsubmit--;
      return this;
    }

    // A submit() call should submit the queue
    if (args.length <= 0) {
      // Check if parent needs to be submitted
      if (
        this.parent &&
        this.parent._submitqueue &&
        this.parent._submitqueue.length > 0
      ) {
        this.parent.submit();
      }

      let queue = this._submitqueue;
      if (!this._haltsubmit && queue.length > 0) {
        // Submit queue
        this._submitqueue = [];
        for (let i = 0; i < queue.length; i++) {
          let queueItem = queue[i];
          if (queueItem.element._submitqueue && queueItem.element !== this) {
            queueItem.element.submit();
          }

          if (queueItem.value instanceof EditorElement) {
            queueItem.value.submit();
            this.editor.submit(
              queueItem.key + '.' + queueItem.param,
              queueItem.value,
              queueItem.reload
            );
          } else {
            this.editor.submit(
              queueItem.key + '.' + queueItem.param,
              queueItem.value,
              queueItem.reload
            );
          }
        }

        setTimeout(function () {
          element.editor.submit();
        });
      }
      this.trigger('submit', queue);
      return this;
    }

    if (!element) {
      element = this;
    }

    if (!this._haltsubmit && !this._modified) {
      let parent: EditorElement = this;
      while (parent.parent && !parent._modified) {
        parent._modified = true;
        parent = parent.parent;
      }
    }

    this._submitqueue = this._submitqueue || [];
    this._submitqueue.push({
      element: element,
      key: element.key,
      param: param,
      value: value,
      reload: reload,
    });

    if (!this._haltsubmit) {
      this.submit();
    }

    return this;
  }

  //
  //
  //
  async goTo(
    path: string[],
    parent?: EditorElement,
    currentpathname?: string,
    param?
  );
  async goTo(
    path?: string[],
    parent?: EditorElement,
    currentpathname?: string,
    param?
  );
  async goTo(...args: any[]) {
    let that = this;
    let path = args[0];
    let parent = args[1];
    let currentpathname = args[2];
    let param = args[3];

    await this.editor.ready();

    // If waiter, clearTimeout(waiter)
    if (that.fetched === false) {
      if (parent) {
        parent.$pane.addClass('loadingChild');
      }
      this.editor.fetchElement(that._data._id, function (err) {
        that.updateUI();
        that.goTo(path, parent, currentpathname, param);
        parent.$pane.removeClass('loadingChild');
      });
      return;
    }
    if (that.fetchedChildren === false) {
      this.editor.fetchElement(that._data._id, function (err) {
        that.updateUI();
      });
    }

    if (typeof path === 'undefined' && !parent) {
      let gotoPath = [];
      let parent: EditorElement = that;
      while (parent.parent) {
        let str = '';
        if (parent.parentParam) str += parent.parentParam + ':';
        str += parent.key;
        if (str) gotoPath.unshift(str);
        parent = parent.parent;
      }
      parent.goTo(gotoPath, undefined, undefined, param);
      return;
    }

    // Mark this element as active
    let isAlreadyActive = this._activeElement;
    this._activeElement = true;

    // Initialize the form pane
    if (!this.$pane) {
      let $rendered = this.render('_form');

      // Compile $pane
      this.$pane = $('<div class="formPane"></div>');
      this.$pane.append($rendered);

      if (!parent) {
        this.$pane.addClass('root');
      }

      //
      this.$formHeader = this.$pane.find('.formHeader');
      this.$form = this.$pane.find('.elementBody');

      // Create a container for sub-panes
      this.$paneContainer = $('<div class="paneContainer"></div>');
      this.$pane.append(this.$paneContainer);

      if (parent) {
        this.$formHeader.find('.close').on('click', function () {
          parent.goTo(undefined, undefined, undefined);
        });
      } else {
        this.$formHeader.addClass('first');
      }

      if (!this.data('_id')) {
        // Re render when focus is lost
        this.on('dataChange', function checkChange(key, value) {
          if (key !== '_id') {
            return;
          }
          this.off('dataChange', checkChange);

          let rerender = () => {
            let isClickable = this.$formHeader.is('.clickable');
            let isHeaderClosed = this.$formHeader.is('.closed');
            let isPaneClosed = this.$pane.is('.closed');
            let isOpen = this.$formHeader.is('.open');

            let $newRender = this.render('_form');
            this.$pane.children(':not(.paneContainer)').remove();
            this.$pane.prepend($newRender);
            this.$formHeader = this.$pane.find('.formHeader');
            this.$form = this.$pane.find('.elementBody');
            this.off('dataChange', checkChange);

            if (isClickable) {
              this.$formHeader.addClass('clickable');
            } else {
              this.$formHeader.removeClass('clickable');
            }

            if (isHeaderClosed) {
              this.$formHeader.addClass('closed');
              this.$formHeader.on('click', () => {
                this.goTo(undefined, undefined, undefined);
              });
            } else {
              this.$formHeader.removeClass('closed');
            }

            if (isPaneClosed) {
              this.$pane.addClass('closed');
              this.$form.css('display', 'none');
            } else {
              this.$pane.removeClass('closed');
              this.$form.css('display', '');
            }

            if (isOpen) {
              this.$formHeader.addClass('open');
            } else {
              this.$formHeader.removeClass('open');
            }
          }; // rerender

          let focusedElement = document.activeElement;
          if (
            this.$pane?.length > 0 &&
            $.contains(this.$pane[0], focusedElement)
          ) {
            $(focusedElement).on('blur', rerender);
          } else {
            rerender();
          }
        });
      }
    }

    if (!_dom.getContainingWindow(this.$pane[0])) {
      this.editor.trigger('openedPane', this);
    }

    // Append form to form "collection"
    if (!$.contains(document.body, this.$pane[0])) {
      if (parent) parent.$paneContainer.append(this.$pane);
      else this.editor.$form.append(this.$pane);
    }

    // Remove old click-events for header
    this.$formHeader.off('click');

    // We have reached the element that should be shown
    if (!path || path.length < 1) {
      // Avoid skipping
      if (this.editor.$form.scrollTop() > 0) {
        this.editor.$form.animate({ scrollTop: 0 }, 100);
      } else {
        this.editor.$form.scrollTop(0);
      }
      this.$formHeader.parents().each(function () {
        if (!$(this).is(that.editor.$form) && $(this).scrollTop() > 0) {
          $(this).scrollTop(0);
        }
      });

      // This should not be a clickable header (since it is already open)
      this.$formHeader.removeClass('clickable');
      this.$formHeader.removeClass('closed');
      this.$formHeader.addClass('open');
      this.$pane.removeClass('closed');

      // Detach all sub-panes
      let nextElement = this.nextElement;
      let replaceElement = this.replaceElement;

      // Replace an open element
      if (replaceElement) {
        replaceElement.$pane.css({
          position: 'absolute',
          top: 0,
        });

        this.$pane.addClass('replacing');
        this.$pane.width(); // Hack to make class change take effect
        this.$pane.removeClass('replacing');

        replaceElement.$pane.addClass('close');

        _dom.transitionend(replaceElement.$pane[0], () => {
          // Detach all children
          let nextElement = replaceElement;
          let cnext: EditorElement;
          nextElement.$pane.find('.listItem.hover').trigger('click');
          while (nextElement) {
            nextElement.editor.trigger('closedPane', nextElement);
            nextElement._activeElement = false;
            nextElement.$pane.detach().show();
            nextElement.$form.show();
            nextElement.unbindEvents(nextElement.$pane);
            nextElement.$pane = undefined;
            nextElement.$form = undefined;
            cnext = nextElement.nextElement;
            nextElement.nextElement = undefined;
            nextElement = cnext;
          }
          updatePerfectScroll(that.$pane.closest('.ps'));

          if (replaceElement.$pane) {
            replaceElement.$pane.removeClass('close');
            replaceElement.$pane.css({
              position: '',
              top: '',
            });
          }
        });

        this.replaceElement = undefined;
      }

      // Zoom in
      else if (!nextElement) {
        if (parent) {
          let $panes = this.$pane.closest('#panes');
          parent.$form.css({
            position: 'absolute',
            top: parent.$formHeader.outerHeight(),
          });
          this.$pane.css({
            height:
              $panes.height() -
              (parent.$form.offset().top - $panes.offset().top),
            opacity: 1,
          });
          this.$pane.css('display', '');
          this.$form.css('display', '');

          this.$pane.addClass('opening');
          await requestAnimationFramePromise();

          // Animate in
          this.$pane.removeClass('opening');
          _dom.transitionend(this.$pane[0], () => {
            // Remove parent
            parent.$form.css({
              position: '',
              top: '',
              display: 'none',
            });

            // Reset new pane height
            that.$pane.css('height', '');
            updatePerfectScroll(that.$pane.closest('.ps'));

            // Focus title bar and animate
            let $title = that.$pane.find('input.title');
            if ($title.length > 0 && !$title.val()) {
              $title.one('mouseover change keydown click', function () {
                $title.clearQueue().stop().css('backgroundColor', '');
              });
              $title
                .first()
                .trigger('focus')
                .stop()
                .animate(
                  {
                    backgroundColor: '#d0d0d0',
                  },
                  400
                )
                .animate(
                  {
                    backgroundColor: '#ffffff',
                  },
                  400
                )
                .animate(
                  {
                    backgroundColor: '#d0d0d0',
                  },
                  400
                )
                .animate(
                  {
                    backgroundColor: '#ffffff',
                  },
                  2000,
                  function () {
                    $title.css('backgroundColor', '');
                  }
                );
            }
          });
        }
        // Show immediately since there is no parent
        else {
          this.$form.css('position', '');
          this.$form.show();
          this.$pane.show();
          updatePerfectScroll(this.$pane.closest('.ps'));
        }
      }

      // We are zooming out (going "back")
      else {
        let $nextElementPane = nextElement.$pane;
        $nextElementPane.addClass('close');

        let $panes = this.$pane.closest('#panes');
        this.$form.css({
          position: 'absolute',
          top: this.$formHeader.outerHeight(),
        });
        this.$pane.css({
          height:
            $panes.height() - (this.$form.offset().top - $panes.offset().top),
          opacity: 1,
        });
        this.$pane.css('display', '');
        this.$form.css('display', '');
        await requestAnimationFramePromise(2);
        _dom.transitionend($nextElementPane[0], () => {
          this.$form?.css({
            display: '',
            position: '',
            top: '',
          });
          this.$pane?.css({
            height: '',
            opacity: '',
          });
          $nextElementPane.detach();
          $nextElementPane.removeClass('close');
          $nextElementPane.find('.group.dropped').each(function () {
            $(this).removeClass('dropped').removeClass('hover');
          });

          // Detach all children
          nextElement.$pane.find('.listItem.hover').trigger('click');
          while (nextElement) {
            nextElement.$form.css({
              display: '',
              position: '',
              top: '',
            });
            nextElement.$pane.css({
              display: '',
              opacity: '',
              marginLeft: '',
              height: '',
            });
            nextElement.editor.trigger('closedPane', nextElement);
            nextElement._activeElement = false;
            if (nextElement.$pane) {
              nextElement.$pane.detach();
              nextElement.unbindEvents(nextElement.$pane);
              nextElement.$pane = undefined;
              nextElement.$form = undefined;
            }
            let cnext = nextElement.nextElement;
            nextElement.nextElement = undefined;
            nextElement = cnext;
          }
          that.nextElement = undefined;

          updatePerfectScroll(that.$pane.closest('.ps'));
        });
      }

      this.editor.currentForm = this;
      this.activateUrl(undefined, undefined, param);

      return;
    }

    // This is not the last path requested
    else {
      // Make header clickable
      this.$formHeader.on('click', function () {
        that.goTo(undefined, undefined, undefined);
      });
      this.$formHeader.addClass('clickable');
      this.$formHeader.addClass('closed');
      this.$formHeader.removeClass('open');
      this.$pane.addClass('closed');
      this.$form.css('position', 'absolute');
      if (parent) {
        parent.$form.hide();
      }
    }

    let nextpath: string;
    let skipsub = this.entitydata('skipsub');

    if (skipsub) {
      if (!Array.isArray(skipsub)) {
        skipsub = [skipsub];
      } else {
        skipsub = skipsub.slice();
      }

      for (let i = 0; i < skipsub.length; i++) {
        if (typeof skipsub[i] === 'object') {
          skipsub[i] = this.value(skipsub[i].key) || skipsub[i].fallback;
        }
      }

      for (let i = 0; i < skipsub.length && path.length > 0; i++) {
        // If a : is in path, it's probably a direct link from the UI
        if (path[0].indexOf(':') < 0) {
          path.shift();
        }
      }
    }

    // Look for entities pathprefix
    let editorData = this.editor.editorData;
    if (
      path.length > 0 &&
      editorData.template &&
      editorData.template[this.template()]
    ) {
      $.each(editorData.template[this.template()], function (key, entity) {
        let entityPathPrefixArray = entity.pathprefix;
        let remove = 0;
        if (!entityPathPrefixArray) {
          return;
        }
        if (!Array.isArray(entityPathPrefixArray)) {
          entityPathPrefixArray = [entityPathPrefixArray];
        } else {
          entityPathPrefixArray = entityPathPrefixArray.slice();
        }
        for (let i = 0; i < entityPathPrefixArray.length; i++) {
          let thisArray = entityPathPrefixArray[i];
          if (typeof thisArray !== 'string') {
            entityPathPrefixArray[i] =
              that.value(thisArray.key) || thisArray.fallback;
          }
        }

        for (let i = 0; i < entityPathPrefixArray.length; i++) {
          let thisEntityPathPrefix = entityPathPrefixArray[i];
          if (typeof thisEntityPathPrefix !== 'string') {
            continue;
          }
          let entityPathPrefix = thisEntityPathPrefix.split(',');
          if (entityPathPrefix.indexOf(path[i]) < 0) {
            return;
          } else {
            remove++;
          }
        }
        path.splice(0, remove);
        return false;
      });
    }
    nextpath = path.shift() || '';

    let colon = nextpath.indexOf(':');
    let parameter = colon >= 0 ? nextpath.substring(0, colon) : undefined;
    let identifier = colon >= 0 ? nextpath.substring(colon + 1) : nextpath;
    colon = identifier.indexOf(':');
    let idfield = colon >= 0 ? identifier.substring(0, colon) : undefined;
    let _id = colon >= 0 ? identifier.substring(colon + 1) : identifier;

    let parameterArray: string[];
    if (parameter) parameterArray = [parameter];
    else parameterArray = Object.keys(this.related || {});

    // Hack, to lookup demosites last
    for (let i = 0; i < parameterArray.length; i++) {
      if (parameterArray[i] === 'demosite') {
        parameterArray.splice(i--, 1);
        parameterArray.push('demosite');
        break;
      }
    }

    // Search children for correct id
    let nextElement: EditorElement;
    outer: for (let innerparam of parameterArray) {
      let relations = that.related[innerparam];
      for (let i = 0; i < relations.length; i++) {
        let relation = relations[i];
        let searchelement = relation.element;
        let catcharg = searchelement.entitydata('catcharg');

        if (
          // idfield given from /path/
          (idfield && searchelement.data(idfield) == _id) ||
          // idfield not given, use
          (!idfield && catcharg && searchelement.data(catcharg) == _id) ||
          // idfield not given, this could be the element key
          (!idfield && searchelement.key == _id)
        ) {
          nextElement = await searchelement.independent(that, innerparam);
          //console.log('nextElement clone: ' + nextElement.data('title') + ' : ' + nextElement.data('_id'));
          parameter = innerparam;
          _id = nextElement.key;
          break outer;
        }
      }
    }

    if (!nextElement) {
      this.goTo([], undefined, undefined, param);
      return;
    }

    // Look for entityVariant for nextElement
    //this.$form.find('id)
    this.$form
      .find(
        '[data-goto="' + nextElement.parentParam + ':' + nextElement.key + '"]'
      )
      .each(function () {
        let $entity = $(this).closest('[data-entityvariant]');
        nextElement.entityVariant = $entity.data('entityvariant');
      });

    // In demo-sites, nextElement will not have parent set to this
    if (!nextElement.parent && !nextElement.isRoot()) {
      nextElement.parent = this;
    }

    // Check if _form has content
    let hasForm = false;
    let nextElementForm = nextElement.entitydata('render')?._form;
    if (typeof nextElementForm === 'function') {
      try {
        let templateCode = nextElementForm({});
        if (templateCode !== '') {
          hasForm = true;
        }
      } catch (e) {
        // When an error occurs, there must be code
        hasForm = true;
      }
    }

    if (!hasForm) {
      // Make element ready before rendering
      await sleep(0);
      if (!nextElement.$formListItem['_formList']) {
        (<any>nextElement)._parent = nextElement.parent;
        (<any>nextElement)._parameter = parameter;
        (<any>nextElement)._templateParent = nextElement.parent;
        //(<any>nextElement)._templateOptions = $target.data('templateoptions') || {};
        nextElement.$formListItem['_formList'] =
          nextElement.render('_formList');
        delete (<any>nextElement)._parent;
        delete (<any>nextElement)._parameter;
        delete (<any>nextElement)._templateParent;
        delete (<any>nextElement)._templateOptions;
      }
      // Open up correct dropdown
      if (nextElement.$formListItem['_formList']) {
        nextElement.$formListItem['_formList']
          .find('.extendlink')
          .trigger('immediateClick');
        nextElement.$formListItem['_formList']
          .closest('.group')
          .find('.dropdownlink:not(.dropped)')
          .trigger('customclick');
      }

      return this.goTo([], undefined, undefined, param);
    } else {
    }

    if (this.nextElement && this.nextElement !== nextElement) {
      nextElement.replaceElement = this.nextElement;
    }
    this.nextElement = nextElement;

    // Set this as nex element's parent
    //if (!this.nextElement.isRoot()) {
    //  this.nextElement.parent = this;
    //}
    this.nextpathname = parameter + ':' + _id;

    // Display next element
    if (this.nextElement)
      this.nextElement.goTo(path, this, this.nextpathname, param);
    else this.goTo(path, parent, currentpathname, param);
  }

  validPayment() {
    let ccMonth;
    let ccYear;
    let ccLastfour;
    let ccExpires: Date;
    let now = new Date();
    if (this.property('stripeId')) {
      ccMonth = this.property('stripeCcMonth');
      ccYear = this.property('stripeCcYear');
      ccLastfour = this.property('stripeCcLastfour');
    }
    ccExpires = new Date(ccYear, ccMonth, 1);
    if (ccLastfour && ccExpires > now) {
      return true;
    } else {
      return false;
    }
  }

  isRoot() {
    return this === this.editor.root;
  }
} //

// Add common prototypes

// Common functions
function escapeselector(str) {
  return str.replace(/([;&,\.\+\*\~':"\!\^#$%@\[\]\(\)=>\|])/g, '\\$1');
}

function scrollParent($DOM: JQuery) {
  let overflowRegex = /(auto|scroll)/;
  let position = $DOM.css('position');
  let excludeStaticParent = position === 'absolute';
  let $scrollParent = $DOM
    .parents()
    .filter(function () {
      let $parent = $(this);
      if (excludeStaticParent && $parent.css('position') === 'static') {
        return false;
      }
      // Workaround for PerfectScrollbar, since it doesn't set overflow
      return (
        $parent.is('.ps') ||
        overflowRegex.test(
          $parent.css('overflow') +
            $parent.css('overflow-y') +
            $parent.css('overflow-x')
        )
      );
    })
    .eq(0);

  return position === 'fixed' || !$scrollParent.length
    ? $($DOM[0].ownerDocument || document).find('html')
    : $scrollParent;
}

/* Find the real offsetParent (even if element is hidden / in an iframe / etc) */
function getRealOffsetParent($DOM: JQuery): JQuery {
  let $offsetParent;
  $DOM.parents().each(function () {
    let $this = $(this);
    let position = ($this.css('position') || '').toLowerCase();
    if (
      position === 'absolute' ||
      position === 'relative' ||
      this.tagName.toUpperCase() === 'HTML'
    ) {
      $offsetParent = $this;
      return false;
    }
  });
  return $offsetParent || null;
}

export default EditorElement;
